/**
 * Unit views for the toon renderer:
 *  - HeroView: a VRM per class (MToon kept + hull outlines), procedural humanoid animation,
 *    KayKit weapons parented to the hand bones.
 *  - MobView: KayKit skeleton per NPC archetype (npcModels.ts), AnimationMixer clips with
 *    crossfades plus an additive procedural layer (run, swing, hit, stun) and a death fall.
 *  - CapsuleView: the placeholder capsule, used when a model failed to load.
 * Every view: root group at the interpolated position with sim facing, an invisible pick box,
 * a blob shadow and a hand glow while casting.
 */
import * as THREE from 'three';
import type { VRM, VRMHumanBoneName } from '@pixiv/three-vrm';
import type { UnitId } from '@keystone/sim';
import { AUTO_SHOT_ABILITY_ID, MELEE_ABILITY_ID } from '@keystone/sim';
import { OFFHAND_CLIP_ID } from './anim/kits/autoattack'; // +autoattack
import { SCHOOL_COLOR } from '../palette';
import { UnitView as PlaceholderUnit } from '../placeholder/units';
import type { RenderUnit } from '../types';
import { AnimDriver, type CueKind, type PoseParams, easeInCubic, swingCurve } from './anim';
import {
  CH_HOP,
  CH_LUNGE,
  CH_SIDE,
  CH_SPIN,
  type ClipSink,
  type CueMarker,
  type FxCueId,
  type HitStopMarker,
  MAX_WINDOWS,
  NCH,
  type RGB,
  type TrailWindow,
  ClipPlayer,
  ch,
  registerAllClips,
  resolveClip,
} from './anim/index';
import { BladeTrails } from './fx/bladeTrails';
import { type IdleInput, IdleLayer, type LegDims } from './anim/idle'; // +idle
import { GlideLayer } from './anim/glide'; // +glide
import { GliderRig } from './glider'; // +glide
import { HERO_VRM, ROLE_VRM, type ToonAssets, type VrmSlot } from './assets';
import { BASE_CLASS_ART, specInfo } from '@keystone/content'; // +classes / +specs
import { baseClassIdOf } from '@keystone/sim';
import { ADVENTURER_GEAR, type MobVisual, npcVisual, npcVisualForTier } from './npcModels';
import { type CreatureRig, buildCreature, poseCreature } from './creatures'; // +field
import { type HullRegistry, addHull, stylize, toonColor } from './style';
import { VrmUpdater } from './vrmFast';
import { groundY } from './ground';
import { attachShadowProxy } from './shadowProxy';
import type { NpcDef } from '@keystone/sim';

export const HERO_HEIGHT = 2.3;
const KAYKIT_HEIGHT = 2.2;
const ARM_DOWN = 1.2;

const toThree = (p: { x: number; y: number }, h: number, out: THREE.Vector3): THREE.Vector3 => out.set(p.x, h, -p.y);

// ---------------------------------------------------------------------------
// Shared per-renderer resources
// ---------------------------------------------------------------------------

function radialTexture(inner: string, outer: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, inner);
  grad.addColorStop(1, outer);
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export class UnitShared {
  readonly blobGeo = new THREE.CircleGeometry(1, 24).rotateX(-Math.PI / 2);
  readonly blobMat: THREE.MeshBasicMaterial;
  readonly glowTex: THREE.CanvasTexture;
  readonly starGeo = new THREE.OctahedronGeometry(0.12, 0);
  readonly starMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(1.0, 0.86, 0.35) });
  readonly hitGeo = new THREE.CylinderGeometry(1, 1, 1, 8);
  readonly hitMat = new THREE.MeshBasicMaterial({ visible: false });
  readonly orbGeo = new THREE.IcosahedronGeometry(0.14, 1);
  readonly orbMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(0.75, 0.55, 1.0) });
  /** Effects intensity multiplier (settings). */
  fx = 1;
  /** Weapon ribbon trails of every hero (one pool; the renderer adds `trails.group` and calls `trails.update`). */
  readonly trails = new BladeTrails();
  /** Every unit's blob shadow in one draw (filled each frame by `placeBlobs`). */
  readonly blobs: THREE.InstancedMesh;
  private blobN = 0;
  private readonly blobM = new THREE.Matrix4();

  constructor() {
    const blobTex = radialTexture('rgba(0,0,0,0.55)', 'rgba(0,0,0,0)');
    this.blobMat = new THREE.MeshBasicMaterial({ map: blobTex, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
    this.glowTex = radialTexture('rgba(255,255,255,1)', 'rgba(255,255,255,0)');
    this.blobs = new THREE.InstancedMesh(this.blobGeo, this.blobMat, 512);
    this.blobs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.blobs.frustumCulled = false;
    this.blobs.renderOrder = 1;
    this.blobs.count = 0;
  }

  /** Start a frame's blob list. */
  beginBlobs(): void {
    this.blobN = 0;
  }

  addBlob(x: number, z: number, r: number, y = 0): void {
    if (this.blobN >= this.blobs.instanceMatrix.count) return;
    const e = this.blobM.elements;
    // Scale (r, 1, r) + translation; written straight into the instance buffer.
    e[0] = r; e[1] = 0; e[2] = 0; e[3] = 0;
    e[4] = 0; e[5] = 1; e[6] = 0; e[7] = 0;
    e[8] = 0; e[9] = 0; e[10] = r; e[11] = 0;
    e[12] = x; e[13] = y + 0.02; e[14] = z; e[15] = 1;
    this.blobs.setMatrixAt(this.blobN++, this.blobM);
  }

  /** Upload only the instances written this frame. */
  endBlobs(): void {
    const im = this.blobs.instanceMatrix;
    this.blobs.count = this.blobN;
    if (this.blobN === 0) return;
    im.clearUpdateRanges();
    im.addUpdateRange(0, this.blobN * 16);
    im.needsUpdate = true;
  }

  dispose(): void {
    this.trails.dispose();
    this.blobs.dispose();
    this.blobGeo.dispose();
    this.blobMat.map?.dispose();
    this.blobMat.dispose();
    this.glowTex.dispose();
    this.starGeo.dispose();
    this.starMat.dispose();
    this.hitGeo.dispose();
    this.hitMat.dispose();
    this.orbGeo.dispose();
    this.orbMat.dispose();
  }
}

export interface UnitCtx {
  assets: ToonAssets;
  hulls: HullRegistry;
  shared: UnitShared;
  npcDef(id: string): NpcDef | undefined;
  mobShadows: boolean;
  /** VRM spring-bone simulation rate cap (Hz; 0 = off). */
  springHz: number;
  /** Ability-clip fx markers and hit-stops (optional: without it clips only animate). */
  clipFx?: ClipFxHooks;
  /** A foot planted (1 left, 2 right) at world point `at` (footstep sound / dust). Must not keep `at`. */
  footstep?(unitId: UnitId, foot: 1 | 2, at: THREE.Vector3, speed: number, height: number): void;
}

/** What hero ability clips ask of the effects system. Called mid-frame; must not allocate. */
export interface ClipFxHooks {
  /** An fx marker at world point `p`, facing `dir` (horizontal unit forward). */
  cue(unitId: UnitId, fx: FxCueId, p: THREE.Vector3, dir: THREE.Vector3, color: RGB, scale: number): void;
  /** A hit-stop marker (the renderer decides whose clips may freeze the screen). */
  hitStop(unitId: UnitId, ms: number, shake: number): void;
}

export interface UnitVisual {
  readonly id: UnitId;
  readonly root: THREE.Group;
  readonly hitbox: THREE.Mesh;
  readonly height: number;
  /** Blob shadow radius (0 = none; the renderer draws all blobs in one instanced draw). */
  readonly blobR: number;
  update(u: RenderUnit, dtS: number, timeMs: number): void;
  /** One-shot cue; heroes pick the ability clip from `abilityId` ('cancel' blends a rejected press out). */
  cue(kind: CueKind, abilityId?: string): void;
  /** True while an ability clip draws this unit's weapon trail (fx skips its synthetic swing arc). */
  ownsSwing?(): boolean;
  /** World position of the casting hand (falls back to a point in front of the chest). */
  handPos(out: THREE.Vector3): THREE.Vector3;
  /**
   * +impact: a hit landed on this unit. `flinch` 0..1.5 (knock / squash / stagger), `flash` 0..1
   * (mesh flash), (dirX, dirZ) the blow's direction in three.js world space, `color` a flash tint.
   */
  impact?(flinch: number, flash: number, dirX: number, dirZ: number, color: RGB | null): void;
  /** +impact: hold the death fall up to `s` seconds (a killing blow is still in flight). */
  deferDeath?(s: number): void;
  /** +impact: the held killing blow landed: fall now. */
  releaseDeath?(): void;
  /** +impact: seconds until the playing strike clip for `abilityId` makes contact, or -1. */
  contactIn?(abilityId: string): number;
  /** +impact: the rig to copy for dodge afterimages (heroes), or null. */
  ghostSource?(): THREE.Object3D | null;
  dispose(): void;
}

// +impact: hit-reaction springs (knock-back along the blow, squash) and the death slide.
const KNOCK_V = 3.4;
const KNOCK_K = 150;
const KNOCK_C = 2 * Math.sqrt(KNOCK_K) * 0.68;
const KNOCK_MAX = 0.36;
const SQUASH_K = 300;
const SQUASH_C = 2 * Math.sqrt(SQUASH_K) * 0.45;
const DEATH_SLIDE = 0.6;
/** Peak emissive added by a hit flash (moderate: a flash, never a white-out). */
export const FLASH_MAX = 0.34;

// ---------------------------------------------------------------------------
// Common base: root, blob, hitbox, hand glow, stun stars
// ---------------------------------------------------------------------------

export abstract class BaseView implements UnitVisual {
  readonly root = new THREE.Group();
  readonly hitbox: THREE.Mesh;
  readonly driver: AnimDriver;
  readonly blobR: number;
  /** Faces +Z model space toward sim facing (+X). */
  protected readonly body = new THREE.Group();
  /** Death fall pivot (inside body). */
  protected readonly fall = new THREE.Group();
  private readonly glow: THREE.Sprite;
  private readonly glowMat: THREE.SpriteMaterial;
  private readonly stars = new THREE.Group();
  private lastPos = new THREE.Vector3(Infinity, 0, 0);
  protected readonly tmp = new THREE.Vector3();
  // +impact: reaction state (world-space knock offset/velocity, squash, last blow direction).
  private kx = 0;
  private kz = 0;
  private kvx = 0;
  private kvz = 0;
  private sq = 0;
  private sqv = 0;
  private dirX = 0;
  private dirZ = 0;
  private deadSeen = false;
  private landed = false;
  protected flash = 0;
  protected readonly flashColor = new THREE.Color(1, 1, 1);
  /** Mobs squash on hits; heroes only get knocked (their clips own the body pivot). */
  protected squashable = false;

  constructor(readonly id: UnitId, readonly height: number, radius: number, protected readonly ctx: UnitCtx) {
    this.driver = new AnimDriver(hashId(id) * 10, height);
    this.body.rotation.y = Math.PI / 2;
    this.body.add(this.fall);
    this.root.add(this.body);
    // Blob shadow: drawn by the shared instanced mesh (UnitShared.blobs) at root's position.
    this.blobR = Math.max(0.6, radius * 1.1);
    const pickR = Math.max(0.9, radius * 0.9);
    this.hitbox = new THREE.Mesh(ctx.shared.hitGeo, ctx.shared.hitMat);
    this.hitbox.scale.set(pickR, height + 0.4, pickR);
    this.hitbox.position.y = (height + 0.4) / 2;
    this.hitbox.userData['unitId'] = id;
    this.root.add(this.hitbox);
    this.glowMat = new THREE.SpriteMaterial({ map: ctx.shared.glowTex, color: 0xffffff, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 });
    this.glow = new THREE.Sprite(this.glowMat);
    this.glow.visible = false;
    this.root.add(this.glow);
    for (let i = 0; i < 3; i++) this.stars.add(new THREE.Mesh(ctx.shared.starGeo, ctx.shared.starMat));
    this.stars.visible = false;
    this.root.add(this.stars);
  }

  /** World position of the casting hand, or null to use a point in front of the chest. */
  protected abstract handWorld(out: THREE.Vector3): THREE.Vector3 | null;
  protected abstract animate(u: RenderUnit, p: PoseParams, dtS: number): void;
  protected onTeleport(): void {}

  cue(kind: CueKind, _abilityId?: string): void {
    this.driver.cue(kind);
  }

  impact(flinch: number, flash: number, dirX: number, dirZ: number, color: RGB | null): void {
    this.driver.cue('hit', Math.min(1.3, 0.55 + 0.75 * flinch));
    const l = Math.hypot(dirX, dirZ);
    if (l > 1e-4) {
      this.dirX = dirX / l;
      this.dirZ = dirZ / l;
      this.kvx += this.dirX * flinch * KNOCK_V;
      this.kvz += this.dirZ * flinch * KNOCK_V;
    }
    if (this.squashable) this.sqv += flinch * 7;
    if (flash > this.flash) {
      this.flash = flash;
      if (color) this.flashColor.setRGB(0.5 + 0.5 * color[0], 0.5 + 0.5 * color[1], 0.5 + 0.5 * color[2]);
      else this.flashColor.setRGB(1, 1, 1);
    }
  }

  deferDeath(s: number): void {
    this.driver.deferDeath(s);
  }

  releaseDeath(): void {
    this.driver.releaseDeath();
  }

  /** Mesh flash (0 = off). Mobs tint their materials; others ignore it. */
  protected applyFlash(_f: number): void {}

  /** +impact: springs, death slide and flash for this frame (after the pose). */
  private react(p: PoseParams, dtS: number): void {
    let left = Math.min(0.05, dtS);
    while (left > 1e-6) {
      const h = Math.min(left, 1 / 90);
      left -= h;
      this.kvx += (-KNOCK_K * this.kx - KNOCK_C * this.kvx) * h;
      this.kvz += (-KNOCK_K * this.kz - KNOCK_C * this.kvz) * h;
      this.kx += this.kvx * h;
      this.kz += this.kvz * h;
      this.sqv += (-SQUASH_K * this.sq - SQUASH_C * this.sqv) * h;
      this.sq += this.sqv * h;
    }
    this.kx = Math.max(-KNOCK_MAX, Math.min(KNOCK_MAX, this.kx));
    this.kz = Math.max(-KNOCK_MAX, Math.min(KNOCK_MAX, this.kz));
    this.sq = Math.max(-0.6, Math.min(0.8, this.sq));
    // Death: a pop (flash + squash) as the fall starts, a slide along the killing blow, a squash on landing.
    let wx = this.kx;
    let wz = this.kz;
    if (p.dead > 0) {
      if (!this.deadSeen) {
        this.deadSeen = true;
        this.flash = Math.max(this.flash, 0.75);
        this.flashColor.setRGB(1, 1, 1);
        if (this.squashable) this.sqv -= 5;
      }
      if (p.dead >= 0.97 && !this.landed) {
        this.landed = true;
        if (this.squashable) this.sqv += 4;
      }
      const t = 1 - (1 - Math.min(1, p.dead * 1.4)) ** 3;
      wx += this.dirX * DEATH_SLIDE * t;
      wz += this.dirZ * DEATH_SLIDE * t;
    } else if (this.deadSeen) this.deadSeen = this.landed = false;
    const yaw = this.root.rotation.y + this.body.rotation.y;
    const c = Math.cos(yaw);
    const sn = Math.sin(yaw);
    this.fall.position.x = wx * c - wz * sn;
    this.fall.position.z = wx * sn + wz * c;
    if (this.squashable) {
      const q = this.sq;
      this.fall.scale.set(1 + 0.12 * q, 1 - 0.22 * q, 1 + 0.12 * q);
    }
    if (this.flash > 0.002) {
      this.flash *= Math.exp(-dtS * 15);
      this.applyFlash(this.flash);
    } else if (this.flash > 0) {
      this.flash = 0;
      this.applyFlash(0);
    }
  }

  handPos(out: THREE.Vector3): THREE.Vector3 {
    const hw = this.handWorld(out);
    if (hw) return hw;
    this.root.updateMatrixWorld(true);
    return out.set(0.6, this.height * 0.6, 0).applyMatrix4(this.root.matrixWorld);
  }

  /** +glide: the flight velocity before the gait saw it (flyers don't run: their speed is zeroed for the gait). */
  protected airVel = { x: 0, y: 0 };

  update(u: RenderUnit, dtS: number, _timeMs: number): void {
    // Stand on the (visual) floor: raised rooms, stairs, sunken pits.
    toThree(u.pos, groundY(u.pos.x, u.pos.y) + (u.jumpH ?? 0), this.root.position); // +jump: the hop's arc
    // +glide: in free flight the legs don't run (the glide pose hangs them); a platform walker does.
    this.airVel.x = u.vel?.x ?? 0;
    this.airVel.y = u.vel?.y ?? 0;
    if (u.glide || (u.z !== undefined && !u.perched && (u.jumpH ?? 0) > 0.3)) {
      u.speed = 0;
      u.vel = { x: 0, y: 0 };
    }
    if (this.lastPos.distanceToSquared(this.root.position) > 100) {
      this.onTeleport();
      this.driver.snapBody(u.facing);
    }
    this.lastPos.copy(this.root.position);
    const p = this.driver.update(u, dtS);
    // The rendered body heading eases toward the facing (no pops; strafe / turn-in-place, gait.ts).
    this.root.rotation.y = p.bodyYaw;
    // +audio: no footfalls mid-air (the jump / land cues cover the hop).
    if (p.plant && u.alive && (u.jumpH ?? 0) < 0.05) this.ctx.footstep?.(this.id, p.plant, this.root.position, u.speed ?? 0, this.height);
    this.animate(u, p, dtS);
    this.fall.rotation.x = -Math.PI / 2 * easeInCubic(p.dead) * 0.98;
    this.fall.position.y = Math.sin(p.dead * Math.PI) * 0.15;
    this.react(p, dtS);
    this.hitbox.scale.y = (this.height + 0.4) * (u.alive ? 1 : 0.35);

    // Hand glow while casting/channelling.
    const fx = this.ctx.shared.fx;
    if (p.cast > 0.05 && fx > 0) {
      this.root.updateMatrixWorld(true);
      const hw = this.handWorld(this.tmp) ?? this.tmp.set(0.6, this.height * 0.6, 0).applyMatrix4(this.root.matrixWorld);
      this.glow.position.copy(this.root.worldToLocal(hw));
      const pulse = 0.85 + 0.15 * Math.sin(p.t * 9);
      const s = (0.55 + 0.25 * p.cast) * pulse * (this.height / HERO_HEIGHT) ** 0.5;
      this.glow.scale.set(s, s, s);
      this.glowMat.color.set(SCHOOL_COLOR[u.cast?.school ?? 'arcane']);
      this.glowMat.opacity = Math.min(1, 0.75 * p.cast * fx);
      this.glow.visible = true;
    } else this.glow.visible = false;

    // Stun stars.
    this.stars.visible = p.stun > 0.3 && u.alive;
    if (this.stars.visible) {
      const stars = this.stars.children;
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i]!;
        const a = p.t * 4 + (i * Math.PI * 2) / 3;
        s.position.set(Math.cos(a) * 0.45, this.height + 0.25 + Math.sin(p.t * 6 + i) * 0.05, Math.sin(a) * 0.45);
        s.rotation.y = a * 2;
      }
    }
  }

  dispose(): void {
    this.glowMat.dispose();
    this.root.removeFromParent();
  }
}

function hashId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 997;
  return h / 997;
}

// ---------------------------------------------------------------------------
// VRM heroes
// ---------------------------------------------------------------------------

interface WeaponSpec {
  file: string;
  node: string;
  hand: 'rightHand' | 'leftHand';
  /**
   * How it's held: 'grip' = handle through the closed fist, blade out of the thumb side (swords,
   * wands, staff); 'shield' = strapped on the back of the hand/forearm, face outward.
   */
  hold: 'grip' | 'shield';
  /** Uniform scale of the KayKit node (KayKit gear is chibi-sized next to a VRoid hero). */
  scale: number;
  /** Weapon-local Y (KayKit units) that sits in the fist (0 = the node's own handle point). */
  grip?: number;
  /** +classes: a procedural weapon instead of a KayKit node (`file` / `node` are then labels). +specs: spec weapons. */
  proc?: ProcWeaponKind;
  /** +classes: weapon long axis in the grip frame (x fingers, y thumb side, z back of the fist); default +y. */
  axis?: readonly [number, number, number];
}

/** Weapons per class (role fallback). Edit freely. */
export const HERO_WEAPONS: Record<string, WeaponSpec[]> = {
  warden: [
    { file: 'Knight', node: '1H_Sword', hand: 'rightHand', hold: 'grip', scale: 0.62 },
    { file: 'Knight', node: 'Round_Shield', hand: 'leftHand', hold: 'shield', scale: 0.8 },
  ],
  lightbinder: [{ file: 'Mage', node: '2H_Staff', hand: 'rightHand', hold: 'grip', scale: 0.85, grip: 0.45 }],
  blademaster: [
    { file: 'Knight', node: '1H_Sword', hand: 'rightHand', hold: 'grip', scale: 0.62 },
    { file: 'Knight', node: '1H_Sword_Offhand', hand: 'leftHand', hold: 'grip', scale: 0.62 },
  ],
  stormcaller: [{ file: 'Mage', node: '1H_Wand', hand: 'rightHand', hold: 'grip', scale: 0.6 }],
  // +classes: the Ranger's bow (a KayKit crossbow stands in until a bow model exists).
  ranger: [{ file: 'proc', node: 'recurve_bow', hand: 'leftHand', hold: 'grip', scale: 0.62, proc: 'recurve_bow', axis: [0.6, 0, 0.8] }],
};
const ROLE_CLASS: Record<string, string> = { tank: 'warden', healer: 'lightbinder', dps: 'blademaster' };

/**
 * +specs: what a specialisation's weapon (SpecUi.weapon) does to the base kit's: hide base holders
 * whose name matches, add procedural weapons in the spec's tint. Unknown weapons keep the base look.
 */
const SPEC_WEAPONS: Readonly<Record<string, { hide?: RegExp; add: WeaponSpec[] }>> = {
  tower_shield: { hide: /shield/i, add: [{ file: 'proc', node: 'tower_shield', hand: 'leftHand', hold: 'shield', scale: 0.85, proc: 'tower_shield' }] },
  twin_greatswords: {
    hide: /sword|shield/i,
    add: [
      { file: 'proc', node: 'greatsword', hand: 'rightHand', hold: 'grip', scale: 0.62, proc: 'greatsword' },
      { file: 'proc', node: 'greatsword_off', hand: 'leftHand', hold: 'grip', scale: 0.62, proc: 'greatsword' },
    ],
  },
  red_greatsword: { hide: /sword|shield/i, add: [{ file: 'proc', node: 'greatsword', hand: 'rightHand', hold: 'grip', scale: 0.7, proc: 'greatsword' }] },
  revolver: { hide: /bow|crossbow/i, add: [{ file: 'proc', node: 'revolver', hand: 'rightHand', hold: 'grip', scale: 0.62, proc: 'revolver' }] },
  // +mage-specs: a staff instead of the wand (the holder names keep "Staff": trails run from the head).
  storm_staff: { hide: /wand|staff/i, add: [{ file: 'proc', node: 'Storm_Staff', hand: 'rightHand', hold: 'grip', scale: 0.6, proc: 'storm_staff' }] },
  ice_staff: { hide: /wand|staff/i, add: [{ file: 'proc', node: 'Ice_Staff', hand: 'rightHand', hold: 'grip', scale: 0.6, proc: 'ice_staff' }] },
  ember_staff: { hide: /wand|staff/i, add: [{ file: 'proc', node: 'Ember_Staff', hand: 'rightHand', hold: 'grip', scale: 0.6, proc: 'ember_staff' }] },
  // +rogue-specs: Shadowdancer / Mirage twin daggers; the Venomwright's dagger and a glowing vial.
  twin_daggers: {
    hide: /sword|shield/i,
    add: [
      { file: 'proc', node: 'dagger', hand: 'rightHand', hold: 'grip', scale: 0.62, proc: 'dagger' },
      { file: 'proc', node: 'dagger_off', hand: 'leftHand', hold: 'grip', scale: 0.62, proc: 'dagger' },
    ],
  },
  vials: {
    hide: /sword|shield/i,
    add: [
      { file: 'proc', node: 'dagger', hand: 'rightHand', hold: 'grip', scale: 0.62, proc: 'dagger' },
      { file: 'proc', node: 'vial', hand: 'leftHand', hold: 'grip', scale: 0.62, proc: 'vial' },
    ],
  },
  // +acolyte-specs: the Acolyte's staff, re-forged per spec (a sunburst mace-staff, a blood thorn staff, a hollow-eyed staff).
  censer: { hide: /staff|wand/i, add: [{ file: 'proc', node: 'Censer_Staff', hand: 'rightHand', hold: 'grip', scale: 0.6, proc: 'censer' }] },
  blood_censer: { hide: /staff|wand/i, add: [{ file: 'proc', node: 'Blood_Staff', hand: 'rightHand', hold: 'grip', scale: 0.6, proc: 'blood_censer' }] },
  tome: { hide: /staff|wand/i, add: [{ file: 'proc', node: 'Hollow_Staff', hand: 'rightHand', hold: 'grip', scale: 0.6, proc: 'tome' }] },
};

/** Spec holders added to a pooled VRM slot (removed again when the slot is dressed for another unit). */
const SPEC_DRESS = new WeakMap<VrmSlot, THREE.Object3D[]>();

/** +mage-specs: spec weapons held upright like the Lightbinder's staff (the staff-hold arm pose). */
const STAFF_WEAPONS: ReadonlySet<string> = new Set(['storm_staff', 'ice_staff', 'ember_staff', 'censer', 'blood_censer', 'tome']); // +acolyte-specs: the three Acolyte staves

/**
 * +specs: dress a prepared hero for its specialisation: swap / add weapons in the spec's tint and a
 * faint aura ring at the feet. Idempotent per slot (slots are pooled across runs). Returns the
 * weapon holders the VRM updater must keep in step.
 */
function dressSpec(slot: VrmSlot, specId: string | null | undefined): THREE.Object3D[] {
  for (const o of SPEC_DRESS.get(slot) ?? []) o.parent?.remove(o);
  SPEC_DRESS.delete(slot);
  for (const h of slot.weaponHolders) h.visible = true;
  const info = specId ? specInfo(specId) : undefined;
  if (!info) return slot.weaponHolders;
  const tint = new THREE.Color(info.ui.tint);
  const glow = new THREE.Color(info.ui.glow);
  const look = SPEC_WEAPONS[info.ui.weapon];
  const added: THREE.Object3D[] = [];
  if (look) {
    if (look.hide) for (const h of slot.weaponHolders) if (look.hide.test(h.name)) h.visible = false;
    for (const spec of look.add) {
      const holder = attachWeapon(slot, spec, null, tint, glow);
      if (holder) added.push(holder);
    }
  } else {
    // No spec weapon: tint the base weapons' glow edge only (a cheap identity cue).
    for (const h of slot.weaponHolders) {
      if (!h.visible) continue;
      const edge = new THREE.Mesh(new THREE.SphereGeometry(0.06, 6, 4), new THREE.MeshBasicMaterial({ color: glow, transparent: true, opacity: 0.85 }));
      h.add(edge);
      added.push(edge);
    }
  }
  // A faint aura ring at the feet in the spec's colour (moderate: below the bloom threshold).
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.55, 0.78, 32),
    new THREE.MeshBasicMaterial({ color: glow, transparent: true, opacity: 0.28, depthWrite: false, side: THREE.DoubleSide }),
  );
  ring.name = 'spec:aura';
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.03 / Math.max(1e-3, slot.vrm.scene.scale.y);
  ring.scale.setScalar(1 / Math.max(1e-3, slot.vrm.scene.scale.x));
  slot.vrm.scene.add(ring);
  added.push(ring);
  SPEC_DRESS.set(slot, added);
  return [...slot.weaponHolders, ...added.filter((o) => o.name.startsWith('weapon:'))];
}

export function heroVrmFile(u: Pick<RenderUnit, 'classId' | 'role'>): string | null {
  return (u.classId && HERO_VRM[baseClassIdOf(u.classId)]) || (u.role ? (ROLE_VRM[u.role] ?? null) : null);
}

/** Build a weapon holder for `spec` in the matching hand of a prepared rig (null when the rig lacks the hand). */
function attachWeapon(slot: VrmSlot, spec: WeaponSpec, assets: ToonAssets | null, tint?: THREE.Color, glow?: THREE.Color): THREE.Group | null {
  const vrm = slot.vrm;
  const hand = vrm.humanoid.getRawBoneNode(spec.hand);
  const src = spec.proc ? procWeapon(spec.proc, tint, glow) : assets?.node(spec.file, spec.node);
  const frame = handFrame(vrm, spec.hand === 'leftHand' ? 'left' : 'right');
  if (!hand || !src || !frame) return null;
  const holder = new THREE.Group();
  holder.name = `weapon:${spec.node}`;
  if (spec.hold === 'grip') {
    // Handle along the fist (little → index knuckles), guard across it (along the fingers).
    holder.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(frame.fingers, frame.thumbward, frame.fingers.clone().cross(frame.thumbward)));
    holder.position.copy(frame.grip);
  } else {
    // Shield face (+Z) out of the back of the hand, top toward the fingers, centred over the wrist.
    const up = frame.fingers;
    const out = frame.palm.clone().negate();
    holder.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(up.clone().cross(out), up, out));
    holder.position.copy(frame.wrist).addScaledVector(out, (0.15 * spec.scale + 0.05) / frame.scale).addScaledVector(up, 0.02 / frame.scale);
  }
  holder.scale.setScalar(spec.scale / frame.scale);
  const w = src.clone();
  // The node's transform under the KayKit hand slot means nothing here: identity, grip at the origin.
  w.position.set(0, -(spec.grip ?? 0), 0);
  w.quaternion.identity();
  if (spec.axis) w.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(...spec.axis).normalize());
  w.scale.setScalar(1);
  holder.add(w);
  hand.add(holder);
  stylize(holder, null, { outline: false });
  return holder;
}

/** One-time prep of a VRM instance: rest pose, scale, outline hulls, weapons. */
function prepareVrm(slot: VrmSlot, classKey: string, assets: ToonAssets): void {
  if (slot.prepared) return;
  const vrm = slot.vrm;
  const hum = vrm.humanoid;
  calibrateRig(slot);
  setArmsDown(vrm, slot.side);
  vrm.update(0);
  vrm.scene.updateMatrixWorld(true);
  const head = hum.getRawBoneNode('head');
  const h = head ? Math.max(0.5, head.getWorldPosition(new THREE.Vector3()).y * 1.12) : 1.6;
  vrm.scene.scale.setScalar(HERO_HEIGHT / h);
  vrm.scene.updateMatrixWorld(true);

  // Inverted-hull outlines on opaque body/clothes/hair; MToon stays as the surface shader.
  const meshes: THREE.Mesh[] = [];
  vrm.scene.traverse((o) => {
    if ((o as THREE.Mesh).isMesh && !o.userData['hull']) meshes.push(o as THREE.Mesh);
  });
  const skip = /eye|brow|lash|mouth|highlight|tooth|teeth|face/i;
  for (const m of meshes) {
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    const base = mats[0] as THREE.Material & { color?: THREE.Color; map?: THREE.Texture | null };
    // Face parts (eyes, brows, mouth, face skin) sit inside the head and hair silhouette: their
    // shadow is invisible but each one was a shadow-pass draw call.
    if (base && (skip.test(base.name || '') || skip.test(m.name || ''))) m.castShadow = false;
    if (!base || base.transparent || skip.test(base.name || '') || skip.test(m.name || '')) continue;
    slot.hulls.push(addHull(m, base, null, 'unit'));
    // MToon's own outline (material [surface, outline]) is a thinner shell inside our hull, so
    // it's never visible here; drop it (one draw + one shadow draw less per mesh).
    if (mats.length === 2 && (mats[1] as { isOutline?: boolean }).isOutline) m.material = mats[0]!;
  }

  // Weapons in the fists: the grip frame is measured from the hand's own finger bones (VRoid
  // hand bones sit at the wrist with model-specific axes), so nothing assumes a bone convention.
  slot.weaponHolders = [];
  const wantFist = new Set<'leftHand' | 'rightHand'>();
  for (const spec of HERO_WEAPONS[classKey] ?? []) {
    const holder = attachWeapon(slot, spec, assets);
    if (!holder) continue;
    wantFist.add(spec.hand);
    slot.weaponHolders.push(holder);
    holder.traverse((o) => {
      if ((o as THREE.Mesh).isMesh && !o.userData['hull']) {
        const m = o as THREE.Mesh;
        slot.hulls.push(addHull(m, m.material as THREE.Material, null, 'unit'));
      }
    });
  }
  if (classKey === 'stormcaller') {
    const lh = hum.getRawBoneNode('leftHand');
    const frame = handFrame(vrm, 'left');
    if (lh && frame) {
      // Keep the orb a child so it follows the hand; geometry/material are per-VRM (live across runs).
      const orb = new THREE.Mesh(new THREE.IcosahedronGeometry(0.11, 1), new THREE.MeshBasicMaterial({ color: new THREE.Color(0.7, 0.5, 1.0) }));
      orb.name = 'weapon:orb';
      orb.scale.setScalar(1 / frame.scale);
      // Floating just off the open palm.
      orb.position.copy(frame.palmCenter).addScaledVector(frame.palm, 0.16 / frame.scale);
      lh.add(orb);
      slot.weaponHolders.push(orb);
    }
  }
  slot.fist = { left: wantFist.has('leftHand') ? 1 : classKey === 'stormcaller' ? 0.25 : 0.35, right: wantFist.has('rightHand') ? 1 : 0.35 };
  curlFingers(vrm, slot.fist);
  slot.rightHand = hum.getRawBoneNode('rightHand');
  TRAIL_ANCHORS.set(slot, [trailAnchor(vrm, slot, 'right'), trailAnchor(vrm, slot, 'left')]);
  // One shadow draw for the whole body instead of one per opaque part.
  attachShadowProxy(vrm.scene);
  const legRig = measureLegs(vrm); // +idle: leg lengths / hip axes for the idle layer's foot IK (rest pose)
  if (legRig) LEG_RIG.set(vrm, legRig); // +idle
  slot.prepared = true;
  // Sanity check (logged once per model): hands should hang below the shoulders in the rest pose.
  setArmsDown(vrm, slot.side);
  vrm.update(0);
  vrm.scene.updateMatrixWorld(true);
  const rs = wp(hum.getRawBoneNode('rightUpperArm'));
  const rh = wp(slot.rightHand);
  const ok = !!rs && !!rh && rh.y < rs.y;
  console[ok ? 'info' : 'warn'](`[toon] ${classKey} VRM rig: side=${slot.side} fz=${slot.fz} arms ${ok ? 'down ✓' : 'NOT down'}`);
}

const wp = (o: THREE.Object3D | null): THREE.Vector3 | null => (o ? o.getWorldPosition(new THREE.Vector3()) : null);

// +idle: what the idle layer needs from a rig (measured once, in the rest pose).
interface LegRig {
  dims: LegDims;
  /** Normalized hips rest position, and the body's left / forward axes in the hips' parent space (unit). */
  hipRest: THREE.Vector3;
  left: THREE.Vector3;
  fwd: THREE.Vector3;
  /** Yards per hips-parent unit. */
  unit: number;
}
const LEG_RIG = new WeakMap<VRM, LegRig>();

/** +idle: thigh / shin lengths and hip width (yd), and the hips translation frame. Null when the rig lacks legs. */
function measureLegs(vrm: VRM): LegRig | null {
  const hum = vrm.humanoid;
  const hipsN = hum.getNormalizedBoneNode('hips');
  const parent = hipsN?.parent;
  const lu = wp(hum.getRawBoneNode('leftUpperLeg'));
  const ru = wp(hum.getRawBoneNode('rightUpperLeg'));
  const ll = wp(hum.getRawBoneNode('leftLowerLeg'));
  const lf = wp(hum.getRawBoneNode('leftFoot'));
  if (!hipsN || !parent || !lu || !ru || !ll || !lf) return null;
  vrm.scene.updateMatrixWorld(true);
  const outer = vrm.scene.parent ? vrm.scene.parent.getWorldScale(new THREE.Vector3()).x || 1 : 1;
  const unit = (parent.getWorldScale(new THREE.Vector3()).x || 1) / outer;
  const toYd = 1 / outer;
  const left = parent.worldToLocal(lu.clone()).sub(parent.worldToLocal(ru.clone()));
  left.y = 0;
  left.normalize();
  const fwd = left.clone().cross(new THREE.Vector3(0, 1, 0)).normalize();
  return {
    dims: { thigh: lu.distanceTo(ll) * toYd, shin: ll.distanceTo(lf) * toYd, hipHalf: (lu.distanceTo(ru) * toYd) / 2 },
    hipRest: hipsN.position.clone(),
    left,
    fwd,
    unit,
  };
}

/** Where a hand's trail is sampled: two points (blade tip, blade base) in `obj`'s local space. */
interface TrailAnchor {
  obj: THREE.Object3D;
  tip: THREE.Vector3;
  base: THREE.Vector3;
  /** `obj` is a weapon holder (its world matrix is refreshed by the VRM updater); else a raw hand bone. */
  holder: boolean;
}

/** Per VRM instance: [right, left] trail anchors (measured once at prep time). */
const TRAIL_ANCHORS = new WeakMap<VrmSlot, readonly [TrailAnchor | null, TrailAnchor | null]>();

/**
 * Measure a hand's trail anchor. A held weapon uses its mesh bounding box in holder space: the
 * long axis gives the blade, the tip is its far end from the grip (holder origin), and the base
 * sits just above the fist. An empty hand trails from the fingertips.
 */
function trailAnchor(vrm: VRM, slot: VrmSlot, side: 'left' | 'right'): TrailAnchor | null {
  const holder = slot.weaponHolders.find((h) => h.name.startsWith('weapon:') && h.name !== 'weapon:orb' && h.parent === vrm.humanoid.getRawBoneNode(`${side}Hand`) && !/shield/i.test(h.name));
  if (holder) {
    holder.updateWorldMatrix(true, true);
    const inv = holder.matrixWorld.clone().invert();
    const box = new THREE.Box3();
    const part = new THREE.Box3();
    const m = new THREE.Matrix4();
    holder.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || o.userData['hull']) return;
      const g = mesh.geometry;
      if (!g.boundingBox) g.computeBoundingBox();
      part.copy(g.boundingBox!).applyMatrix4(m.multiplyMatrices(inv, mesh.matrixWorld));
      box.union(part);
    });
    if (!box.isEmpty()) {
      const size = box.getSize(new THREE.Vector3());
      const axis = size.x >= size.y && size.x >= size.z ? 0 : size.y >= size.z ? 1 : 2;
      const lo = box.min.getComponent(axis);
      const hi = box.max.getComponent(axis);
      const end = /Staff/.test(holder.name) ? hi : Math.abs(hi) >= Math.abs(lo) ? hi : lo; // anim hook: staves trail from the gem head (+Y), not the longer shaft end
      const c = box.getCenter(new THREE.Vector3());
      const tip = c.clone().setComponent(axis, end);
      const base = c.clone().setComponent(axis, end * 0.18);
      return minRibbon({ obj: holder, tip, base, holder: true });
    }
  }
  const hand = vrm.humanoid.getRawBoneNode(`${side}Hand`);
  const f = handFrame(vrm, side);
  if (!hand || !f) return null;
  const L = f.palmCenter.length() / 0.55;
  return minRibbon({ obj: hand, tip: f.fingers.clone().multiplyScalar(L * 2.1), base: f.fingers.clone().multiplyScalar(L * 0.5), holder: false });
}

/** Shortest tip → base span (world yards): wands and empty hands still leave a readable ribbon. */
const MIN_RIBBON = 0.6;

function minRibbon(a: TrailAnchor): TrailAnchor {
  a.obj.updateWorldMatrix(true, false);
  const len = a.tip.distanceTo(a.base) * a.obj.matrixWorld.getMaxScaleOnAxis();
  if (len > 1e-6 && len < MIN_RIBBON) a.base.sub(a.tip).multiplyScalar(MIN_RIBBON / len).add(a.tip);
  return a;
}

/** A hand's grip frame in the raw hand bone's local space (unit vectors; points in bone units). */
export interface HandFrame {
  /** Wrist → middle knuckle. */
  fingers: THREE.Vector3;
  /** Across the knuckles toward the index finger, orthogonal to `fingers` (a blade's direction out of a fist). */
  thumbward: THREE.Vector3;
  /** Palm normal (out of the palm). */
  palm: THREE.Vector3;
  wrist: THREE.Vector3;
  palmCenter: THREE.Vector3;
  /** Where a held handle's axis passes: under the curled fingers. */
  grip: THREE.Vector3;
  /** Hand bone world scale (world units per bone unit). */
  scale: number;
}

const FINGER = { index: 'IndexProximal', middle: 'MiddleProximal', little: 'LittleProximal' } as const;

/**
 * Measure a hand from its finger bones in the current pose (arms-down rest pose at prep time).
 * The palm normal's sign comes from the rest pose: palms face the body's midline.
 */
export function handFrame(vrm: VRM, side: 'left' | 'right'): HandFrame | null {
  const hum = vrm.humanoid;
  const hand = hum.getRawBoneNode(`${side}Hand`);
  const bone = (n: string): THREE.Object3D | null => hum.getRawBoneNode(`${side}${n}` as VRMHumanBoneName);
  const idx = bone(FINGER.index);
  const mid = bone(FINGER.middle);
  const lit = bone(FINGER.little) ?? bone('RingProximal');
  if (!hand || !idx || !mid || !lit) return null;
  vrm.scene.updateMatrixWorld(true);
  const local = (o: THREE.Object3D): THREE.Vector3 => hand.worldToLocal(o.getWorldPosition(new THREE.Vector3()));
  const wrist = new THREE.Vector3();
  const pm = local(mid);
  const L = pm.length();
  if (L < 1e-6) return null;
  const fingers = pm.clone().normalize();
  const thumbward = local(idx).sub(local(lit));
  thumbward.addScaledVector(fingers, -thumbward.dot(fingers)).normalize();
  const palm = fingers.clone().cross(thumbward).normalize();
  // Palms face the midline in the arms-down rest pose: flip the normal if it points outward.
  const hips = hum.getRawBoneNode('hips');
  if (hips) {
    const hw = hand.getWorldPosition(new THREE.Vector3());
    const toMid = hips.getWorldPosition(new THREE.Vector3()).sub(hw);
    toMid.y = 0;
    const n = palm.clone().transformDirection(hand.matrixWorld);
    if (n.dot(toMid) < 0) palm.negate();
  }
  const palmCenter = fingers.clone().multiplyScalar(L * 0.55);
  const grip = fingers.clone().multiplyScalar(L * 1.02).addScaledVector(palm, L * 0.32);
  const scale = hand.getWorldScale(new THREE.Vector3()).x || 1;
  return { fingers, thumbward, palm, wrist, palmCenter, grip, scale };
}

const FINGER_CHAINS = ['Index', 'Middle', 'Ring', 'Little'] as const;
const FINGER_JOINTS = [['Proximal', 0.9], ['Intermediate', 1.25], ['Distal', 0.9]] as const;

/**
 * Close a hand around a handle (amount 1) or relax it (~0.3). The curl axis/sign is measured:
 * rotate the middle finger's proximal a little and keep the sign that moves its tip into the palm.
 * Finger bones aren't touched by poseVrm, so this sticks (humanoid.update copies it every frame).
 */
function curlFingers(vrm: VRM, fist: { left: number; right: number }): void {
  const hum = vrm.humanoid;
  for (const side of ['left', 'right'] as const) {
    const frame = handFrame(vrm, side);
    const hand = hum.getRawBoneNode(`${side}Hand`);
    const prox = hum.getNormalizedBoneNode(`${side}MiddleProximal`);
    const tipRaw = hum.getRawBoneNode(`${side}MiddleDistal`) ?? hum.getRawBoneNode(`${side}MiddleIntermediate`);
    if (!frame || !hand || !prox || !tipRaw) continue;
    const before = hand.worldToLocal(tipRaw.getWorldPosition(new THREE.Vector3()));
    prox.rotation.set(0, 0, 0.4);
    hum.update();
    vrm.scene.updateMatrixWorld(true);
    const after = hand.worldToLocal(tipRaw.getWorldPosition(new THREE.Vector3()));
    prox.rotation.set(0, 0, 0);
    const sign = after.sub(before).dot(frame.palm) > 0 ? 1 : -1;
    const amt = fist[side];
    for (const f of FINGER_CHAINS) {
      for (const [j, a] of FINGER_JOINTS) {
        hum.getNormalizedBoneNode(`${side}${f}${j}` as VRMHumanBoneName)?.rotation.set(0, 0, sign * a * amt);
      }
    }
  }
  hum.update();
  vrm.scene.updateMatrixWorld(true);
}

/**
 * Measure the normalized rig's conventions from real bone positions instead of assuming
 * VRM0/VRM1 axes: `side` makes "arms down" go down, `fz` makes "forward" go toward the face.
 */
function calibrateRig(slot: VrmSlot): void {
  const vrm = slot.vrm;
  const hum = vrm.humanoid;
  const settle = (): void => {
    vrm.humanoid.update();
    vrm.scene.updateMatrixWorld(true);
  };
  for (const n of POSED_BONES) hum.getNormalizedBoneNode(n)?.rotation.set(0, 0, 0);
  // Arms: rotate the left upper arm by -ARM_DOWN about z and see whether the hand drops.
  const la = hum.getNormalizedBoneNode('leftUpperArm');
  if (la) {
    la.rotation.set(0, 0, -ARM_DOWN);
    settle();
    const sh = wp(hum.getRawBoneNode('leftUpperArm'));
    const hand = wp(hum.getRawBoneNode('leftHand'));
    slot.side = sh && hand && hand.y > sh.y ? -1 : 1;
    la.rotation.set(0, 0, 0);
  }
  // Forward: swing the left thigh by -0.6 about x and see whether the foot moves toward the face (+Z after rotateVRM0).
  const lt = hum.getNormalizedBoneNode('leftUpperLeg');
  if (lt) {
    settle();
    const foot0 = wp(hum.getRawBoneNode('leftFoot'));
    lt.rotation.set(-0.6, 0, 0);
    settle();
    const foot1 = wp(hum.getRawBoneNode('leftFoot'));
    // Model forward in scene-parent space is +Z (rotateVRM0 handled VRM0 files).
    const fwd = new THREE.Vector3(0, 0, 1);
    slot.fz = foot0 && foot1 && foot1.clone().sub(foot0).dot(fwd) < 0 ? -1 : 1;
    lt.rotation.set(0, 0, 0);
  }
  settle();
}

function setArmsDown(vrm: VRM, side: number): void {
  const la = vrm.humanoid.getNormalizedBoneNode('leftUpperArm');
  const ra = vrm.humanoid.getNormalizedBoneNode('rightUpperArm');
  if (la) la.rotation.set(0, 0, -ARM_DOWN * side);
  if (ra) ra.rotation.set(0, 0, ARM_DOWN * side);
}

const POSED_BONES: VRMHumanBoneName[] = [
  'hips', 'spine', 'chest', 'upperChest', 'neck', 'head',
  'leftUpperLeg', 'rightUpperLeg', 'leftLowerLeg', 'rightLowerLeg', 'leftFoot', 'rightFoot',
  'leftUpperArm', 'rightUpperArm', 'leftLowerArm', 'rightLowerArm', 'leftHand', 'rightHand',
  'leftShoulder', 'rightShoulder', // +idle: breathing shrug
];

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** POSED_BONES indices. */
const PB = { hips: 0, spine: 1, chest: 2, upperChest: 3, neck: 4, head: 5, lUpLeg: 6, rUpLeg: 7, lLoLeg: 8, rLoLeg: 9, lFoot: 10, rFoot: 11, lUpArm: 12, rUpArm: 13, lLoArm: 14, rLoArm: 15, lHand: 16, rHand: 17, lShoulder: 18, rShoulder: 19 } as const; // +idle: shoulders
const BONE_CACHE = new WeakMap<VRM, (THREE.Object3D | null)[]>();

/** Normalized bone nodes in POSED_BONES order (looked up once per VRM). */
function posedBones(vrm: VRM): (THREE.Object3D | null)[] {
  let b = BONE_CACHE.get(vrm);
  if (!b) {
    b = POSED_BONES.map((n) => vrm.humanoid.getNormalizedBoneNode(n) ?? null);
    BONE_CACHE.set(vrm, b);
  }
  return b;
}

/** Ability-clip layer for `poseVrm` (anim/clips.ts ClipPlayer output). */
export interface ClipPose {
  /** Semantic offsets (NCH channels, see anim/clips.ts). */
  readonly out: Float32Array;
  /** 0..1: how much each arm's base gestures (guard, staff hold) give way to the clip. */
  readonly armL: number;
  readonly armR: number;
}

const ZERO_POSE = new Float32Array(NCH);
const C_HIPS = ch('hips', 0);
const C_SPINE = ch('spine', 0);
const C_CHEST = ch('chest', 0);
const C_NECK = ch('neck', 0);
const C_HEAD = ch('head', 0);
const C_LUA = ch('leftUpperArm', 0);
const C_RUA = ch('rightUpperArm', 0);
const C_LLA = ch('leftLowerArm', 0);
const C_RLA = ch('rightLowerArm', 0);
const C_LH = ch('leftHand', 0);
const C_RH = ch('rightHand', 0);
const C_LUL = ch('leftUpperLeg', 0);
const C_RUL = ch('rightUpperLeg', 0);
const C_LLL = ch('leftLowerLeg', 0);
const C_RLL = ch('rightLowerLeg', 0);
const C_LF = ch('leftFoot', 0);
const C_RF = ch('rightFoot', 0);

/** Torso bone: [pitch forward, twist left, lean right] in rig axes, on top of base values. */
function torso(b: THREE.Object3D | null, fz: number, C: Float32Array, c: number, px: number, ty: number, lz: number): void {
  if (!b) return;
  b.rotation.set(fz * (px + C[c]!), ty + C[c + 1]!, lz + fz * C[c + 2]!);
}

/**
 * Procedural humanoid pose on VRM normalized bones (absolute each frame, so nothing accumulates).
 * `clip` (heroes): the ability-clip layer. Its offsets are added in semantic space, and its arm
 * weights fade out the base arm gestures. Without it, the legacy swing / spell / cast arm poses
 * of PoseParams apply. No allocation.
 */
export function poseVrm(vrm: VRM, p: PoseParams, side: number, fz: number, classKey: string, clip: ClipPose | null = null, idle: IdleLayer | null = null): void {
  const B = posedBones(vrm);
  for (let i = 0; i < B.length; i++) B[i]?.rotation.set(0, 0, 0);
  // +idle: the idle layer (its offsets are already in `clip.out`) owns breathing, the head, the
  // rest / guard / staff arm poses and the pelvis translation; the legacy terms below switch off.
  const old = idle ? 0 : 1;
  if (idle) {
    const rig = LEG_RIG.get(vrm);
    const hips = B[PB.hips];
    if (rig && hips) {
      const u = 1 / rig.unit;
      hips.position.copy(rig.hipRest).addScaledVector(rig.left, -idle.hipRight * u).addScaledVector(rig.fwd, idle.hipFwd * u);
      hips.position.y += idle.hipUp * u;
    }
    B[PB.lShoulder]?.rotation.set(0, 0, side * idle.shoulder);
    B[PB.rShoulder]?.rotation.set(0, 0, -side * idle.shoulder);
  }
  const C = clip ? clip.out : ZERO_POSE;
  const legacy = clip === null;
  const armL = clip ? clip.armL : 0;
  const armR = clip ? clip.armR : 0;
  // Sweep/elbow rotations about the arm's own vertical axis flip with both conventions.
  const k = fz * side;
  const breath = Math.sin(p.t * 1.8) * old; // +idle
  const alive = 1 - p.dead;
  const stun = p.stun;
  const run = p.run * (1 - stun) * alive;
  const s = Math.sin(p.runPhase);
  const c = Math.cos(p.runPhase);
  const cast = p.cast * (1 - stun) * alive;
  const sw = swingCurve(legacy && p.attackKind === 'swing' ? p.attack : -1);
  const swRaise = sw.raise;
  const swStrike = sw.strike;
  const spell = legacy && p.attackKind === 'spell' && p.attack >= 0 ? Math.sin(p.attack * Math.PI) : 0;
  const combat = p.inCombat ? 1 : 0;
  // Gait (render/toon/gait.ts): backpedal (the cycle runs backwards; shorter steps, upright),
  // side-step (legs spread alternately), turn-in-place shuffle; upper-body twist toward the aim.
  const back = p.gaitBack;
  const sideStep = p.gaitSide;
  const legAmp = 0.7 * (1 - 0.2 * back - 0.55 * sideStep); // +controls: the backpedal stride (gait BACK_STRIDE 0.8)
  const spread = 0.3 * sideStep * run;
  const lSpread = spread * Math.max(0, s * p.sideSign);
  const rSpread = spread * Math.max(0, -s * p.sideSign);
  const shuf = p.shuffle * (1 - run) * alive * (1 - stun);
  const ss = Math.sin(p.shufflePhase);
  const lShuf = shuf * Math.max(0, ss);
  const rShuf = shuf * Math.max(0, -ss);

  // Torso: breathing, run lean (a slight back lean backpedalling), stun slump, hit recoil, (legacy) swing twist, twist, clip offsets.
  torso(B[PB.hips]!, fz, C, C_HIPS, 0, s * 0.1 * run, 0);
  torso(B[PB.spine]!, fz, C, C_SPINE, breath * 0.018 + run * 0.12 * (1 - 1.5 * back) + stun * 0.14 - p.hit * 0.3 + swStrike * 0.18, p.twistSpine - swRaise * 0.25 - spell * 0.15, stun * Math.sin(p.t * 2.6) * 0.08);
  torso(B[PB.chest] ?? B[PB.upperChest]!, fz, C, C_CHEST, breath * 0.025 + stun * 0.1, -s * 0.16 * run * (1 - 0.5 * sideStep), 0);
  torso(B[PB.neck]!, fz, C, C_NECK, 0, 0, 0);
  torso(B[PB.head]!, fz, C, C_HEAD, -breath * 0.015 + stun * 0.38 - p.hit * 0.18 - run * 0.06, p.twistHead + Math.sin(p.t * 0.5) * 0.1 * (1 - run) * old, 0); // +idle: no sine head sway

  // Legs: run cycle + gait + clip [thigh forward, twist, spread] / [knee bend] / [foot pitch].
  const lul = B[PB.lUpLeg];
  if (lul) lul.rotation.set(-fz * (s * legAmp * run + 0.3 * lShuf + C[C_LUL]!), -k * C[C_LUL + 1]!, side * (C[C_LUL + 2]! + lSpread));
  const rul = B[PB.rUpLeg];
  if (rul) rul.rotation.set(-fz * (-s * legAmp * run + 0.3 * rShuf + C[C_RUL]!), k * C[C_RUL + 1]!, -side * (C[C_RUL + 2]! + rSpread));
  const lll = B[PB.lLoLeg];
  if (lll) lll.rotation.x = fz * (run * (0.12 + 1.05 * (1 - 0.35 * back) * Math.max(0, c)) + 0.55 * lShuf + C[C_LLL]!);
  const rll = B[PB.rLoLeg];
  if (rll) rll.rotation.x = fz * (run * (0.12 + 1.05 * (1 - 0.35 * back) * Math.max(0, -c)) + 0.55 * rShuf + C[C_RLL]!);
  const lf = B[PB.lFoot];
  if (lf) lf.rotation.x = -fz * (run * 0.25 * Math.max(0, c) + 0.2 * lShuf + C[C_LF]!);
  const rf = B[PB.rFoot];
  if (rf) rf.rotation.x = -fz * (run * 0.25 * Math.max(0, -c) + 0.2 * rShuf + C[C_RF]!);

  // Arms: down from the T-pose, then run swing, cast pose, attacks.
  let lx = 0;
  let rx = 0;
  let lz = -ARM_DOWN;
  let rz = ARM_DOWN;
  let lBend = 0.18 + 0.05 * breath;
  let rBend = 0.18 + 0.05 * breath;
  lz -= breath * 0.02;
  rz += breath * 0.02;
  // Run swing (opposite to legs; smaller backpedalling / side-stepping).
  const armAmp = 0.55 * (1 - 0.45 * back - 0.4 * sideStep);
  lx += s * armAmp * run;
  rx -= s * armAmp * run;
  lBend += 0.55 * run;
  rBend += 0.55 * run;
  // Staff: forearm forward so the fist (and the staff through it) stands upright; the off-hand
  // lightly on the staff side. Gives way to clips that animate the right arm.
  if (classKey === 'lightbinder' && !idle) { // +idle: the idle style holds the staff
    const hold = alive * (1 - stun) * (1 - run * 0.5) * (1 - armR);
    rBend += 1.25 * hold;
    rz = lerp(rz, ARM_DOWN * 0.92, hold);
  }
  // Combat guard: tank raises the shield arm, dual wielders hold blades forward.
  const guard = combat * (1 - run) * (1 - cast) * alive * (1 - stun) * old; // +idle: the idle layer's combat stance
  if (classKey === 'warden') {
    const g = guard * (1 - armL);
    lx -= 0.7 * g;
    lz = lerp(lz, -0.75, g);
    lBend += 0.7 * g;
  } else if (classKey === 'blademaster') {
    lx -= 0.45 * guard * (1 - armL);
    rx -= 0.45 * guard * (1 - armR);
    lBend += 0.6 * guard * (1 - armL);
    rBend += 0.6 * guard * (1 - armR);
  }
  let ly = 0;
  let ry = 0;
  if (legacy) {
    // Cast / channel: arms swept forward (about the vertical axis) and raised a little.
    const wob = p.channel ? Math.sin(p.t * 7) * 0.06 : 0;
    ly = lerp(ly, 1.05 + wob, cast);
    ry = lerp(ry, 1.15 - wob, cast);
    lx = lerp(lx, -0.3, cast);
    rx = lerp(rx, -0.45, cast);
    lz = lerp(lz, -0.3, cast);
    rz = lerp(rz, 0.25, cast);
    lBend = lerp(lBend, 0.4, cast);
    rBend = lerp(rBend, 0.3, cast);
    // Melee swing: right arm overhead then a fast strike forward-down (off-hand follows for dual wield).
    if (swRaise !== 0 || swStrike !== 0) {
      rx += -2.3 * Math.max(0, swRaise) - 0.9 * swStrike;
      rz = lerp(rz, 0.45, Math.max(0, swRaise));
      rBend += 0.4 * Math.max(0, swRaise);
      if (classKey === 'blademaster') lx += -0.9 * swStrike;
    }
    // Instant spell: right-hand thrust.
    if (spell > 0) {
      ry = lerp(ry, 1.3, spell);
      rx = lerp(rx, -0.3, spell);
      rz = lerp(rz, 0.15, spell);
      rBend = lerp(rBend, 0.05, spell);
    }
  }
  // Clip: [raise forward, sweep across the front, raise sideways] / [elbow bend, forearm twist].
  lx -= C[C_LUA]!;
  ly += C[C_LUA + 1]!;
  lz += C[C_LUA + 2]!;
  rx -= C[C_RUA]!;
  ry += C[C_RUA + 1]!;
  rz -= C[C_RUA + 2]!;
  lBend += C[C_LLA]!;
  rBend += C[C_RLA]!;
  // Hit react: arms flinch out a little.
  lz -= p.hit * 0.15;
  rz += p.hit * 0.15;
  // x: swing/raise (negative = forward), y: forward sweep (left -, right +), z: down from the T-pose.
  B[PB.lUpArm]?.rotation.set(lx * fz, -ly * k, lz * side);
  B[PB.rUpArm]?.rotation.set(rx * fz, ry * k, rz * side);
  const lla = B[PB.lLoArm];
  if (lla) lla.rotation.set(fz * C[C_LLA + 1]!, -lBend * k, 0);
  const rla = B[PB.rLoArm];
  if (rla) rla.rotation.set(fz * C[C_RLA + 1]!, rBend * k, 0);
  // Dead: turn the fists over so held weapons lie flat on the floor instead of standing up.
  const flat = -DEATH_TWIST * easeInCubic(p.dead) * fz;
  B[PB.lHand]?.rotation.set(flat + fz * C[C_LH]!, -k * C[C_LH + 1]!, -side * C[C_LH + 2]!);
  B[PB.rHand]?.rotation.set(flat + fz * C[C_RH]!, k * C[C_RH + 1]!, side * C[C_RH + 2]!);
}

/** Hand twist (rad, about the arm) once dead; the sign was checked so blades fall outward. */
const DEATH_TWIST = 1.4;

let heroSeq = 0;

// Scratch for trail sampling / cue placement (heroes update one at a time).
const TV_TIP = new THREE.Vector3();
const TV_BASE = new THREE.Vector3();
const TV_P = new THREE.Vector3();
const TV_DIR = new THREE.Vector3();
/** Fade when a stun / death / cast end cuts a clip. */
const STOP_FADE = 0.12;
const LOOP_END_FADE = 0.16;
/** Most cue markers one frame can fire per hero (extra ones are dropped). */
const MAX_FRAME_CUES = 8;

export class HeroView extends BaseView implements ClipSink {
  private readonly classKey: string;
  /** +mage-specs: the class key the idle arm pose reads (a staff spec holds its staff like the Lightbinder). */
  private readonly poseKey: string;
  private readonly updater: VrmUpdater;
  /** Ability clip runtime (anim/clips.ts). */
  readonly clips = new ClipPlayer();
  private readonly anchors: readonly [TrailAnchor | null, TrailAnchor | null];
  /** Ribbon handle per open trail window (-1 = none). */
  private readonly trailIds = new Int32Array(MAX_WINDOWS).fill(-1);
  private readonly trailHand = new Uint8Array(MAX_WINDOWS);
  private readonly trailWidth = new Float32Array(MAX_WINDOWS);
  /** Cue markers fired during `clips.update`, placed after the skeleton is posed this frame. */
  private readonly frameCues: (CueMarker | null)[] = new Array(MAX_FRAME_CUES).fill(null);
  private frameCueN = 0;
  /** Can start strikes (alive, not stunned): from the last update. */
  private canAct = true;
  private facing = 0;
  /** +idle: the standing layer (anim/idle.ts) and the clip + idle channel sum handed to poseVrm. */
  readonly idle: IdleLayer;
  private readonly pose = { out: new Float32Array(NCH), armL: 0, armR: 0 };
  private readonly idleIn: IdleInput = { dt: 0, run: 0, cast: 0, inCombat: false, stun: 0, dead: 0, shuffle: 0, armL: 0, armR: 0, clipW: 0 };
  /** +glide: the hanging pose (anim/glide.ts) and the canopy (glider.ts, built on the first glide). */
  readonly glideLayer = new GlideLayer();
  private glider: GliderRig | null = null;
  private glideFacing = NaN;
  private readonly glideTint: string;

  constructor(u: RenderUnit, ctx: UnitCtx, private readonly slot: VrmSlot) {
    super(u.id, HERO_HEIGHT, u.radius, ctx);
    registerAllClips();
    // +classes: a base class borrows its old kit's look (Warrior → Warden, Mage → Stormcaller, …).
    const cls = u.classId ? baseClassIdOf(u.classId) : null; // +specs: "warrior@ironclad" strangers wear the Warrior
    this.classKey = (cls && HERO_WEAPONS[cls] ? cls : null) ?? (cls ? BASE_CLASS_ART[cls] : undefined) ?? ROLE_CLASS[u.role ?? 'dps'] ?? 'blademaster';
    prepareVrm(slot, this.classKey, ctx.assets);
    this.anchors = TRAIL_ANCHORS.get(slot) ?? [null, null];
    this.fall.add(slot.vrm.scene);
    for (const h of slot.hulls) ctx.hulls.unit.add(h);
    slot.vrm.springBoneManager?.reset();
    // Stagger the heroes' spring steps so a 120 Hz frame runs about half of them.
    this.updater = new VrmUpdater(slot.vrm, (heroSeq++ % 4) / 4);
    // The fast path refreshes joint matrices only; keep the weapons' world matrices in step.
    // +specs: the spec's weapons and tint (pooled slots are re-dressed for every hero view).
    this.updater.followers = dressSpec(slot, u.specId);
    const weapon = u.specId ? specInfo(u.specId)?.ui.weapon : undefined;
    this.poseKey = weapon && STAFF_WEAPONS.has(weapon) ? 'lightbinder' : this.classKey; // +mage-specs
    this.idle = new IdleLayer(this.poseKey, hashId(String(u.id)), LEG_RIG.get(slot.vrm)?.dims ?? null); // +idle: per-hero phase seed
    this.glideTint = u.color || '#d98a4a'; // +glide: the canopy carries the class tint
  }

  protected handWorld(out: THREE.Vector3): THREE.Vector3 | null {
    return this.slot.rightHand ? this.slot.rightHand.getWorldPosition(out) : null;
  }

  protected override onTeleport(): void {
    this.updater.reset();
    this.clips.reset(this);
  }

  /**
   * Strikes start here: on the predicted key press (instants) or the sim's cast success.
   * `cancel` (a rejected prediction) blends the matching strike out.
   */
  override cue(kind: CueKind, abilityId?: string): void {
    if (kind === 'hit') {
      this.driver.cue('hit');
      return;
    }
    if (kind === 'cancel') {
      this.clips.cancelIf(abilityId ?? null, this);
      return;
    }
    if (!this.canAct) return;
    // Auto-attacks never cut an ability strike before its impact, nor a cast / channel loop.
    const c = this.clips.clip;
    if ((abilityId === MELEE_ABILITY_ID || abilityId === OFFHAND_CLIP_ID || abilityId === AUTO_SHOT_ABILITY_ID) && c && (c.loop || !this.clips.recovering)) return; // +autoattack: white swings / shots too
    const clip = resolveClip(abilityId, 'strike', this.classKey, kind);
    if (clip) this.clips.play(clip, abilityId ?? null, 'strike', this);
  }

  contactIn(abilityId: string): number {
    const clips = this.clips;
    const c = clips.clip;
    if (!c || clips.phase !== 'strike' || clips.abilityId !== abilityId) return -1;
    const e = clips.elapsed;
    for (let i = 0; i < c.stops.length; i++) if (c.stops[i]!.t > e + 1e-4) return c.stops[i]!.t - e;
    return -1;
  }

  ghostSource(): THREE.Object3D | null {
    return this.root.visible ? this.slot.vrm.scene : null;
  }

  ownsSwing(): boolean {
    const c = this.clips.clip;
    return !!c && c.windows.length > 0 && (this.anchors[0] !== null || this.anchors[1] !== null);
  }

  /** Play a clip directly (debug clip browser). */
  playClip(abilityId: string | null, phase: 'strike' | 'cast' | 'channel', kind: 'swing' | 'spell' = 'swing'): void {
    const clip = resolveClip(abilityId, phase, this.classKey, kind);
    if (clip) this.clips.play(clip, abilityId, phase, this);
  }

  // --- ClipSink -------------------------------------------------------------

  fxCue(cue: CueMarker): void {
    if (this.frameCueN < MAX_FRAME_CUES) this.frameCues[this.frameCueN++] = cue;
  }

  hitStop(m: HitStopMarker): void {
    this.ctx.clipFx?.hitStop(this.id, m.ms, m.shake);
  }

  trailStart(i: number, w: TrailWindow): void {
    if (this.trailIds[i]! >= 0) this.ctx.shared.trails.release(this.trailIds[i]!);
    this.trailIds[i] = this.anchors[w.hand] ? this.ctx.shared.trails.start(w) : -1;
    this.trailHand[i] = w.hand;
    this.trailWidth[i] = w.width;
  }

  trailEnd(i: number): void {
    if (this.trailIds[i]! >= 0) this.ctx.shared.trails.release(this.trailIds[i]!);
    this.trailIds[i] = -1;
  }

  // --------------------------------------------------------------------------

  protected animate(u: RenderUnit, p: PoseParams, dtS: number): void {
    const vrm = this.slot.vrm;
    const clips = this.clips;
    this.facing = u.facing;
    // Interrupts: stuns and death cut the clip (a quick blend back to the base pose).
    this.canAct = u.alive && !u.cc;
    if (!this.canAct && clips.clip) clips.stop(STOP_FADE, this);
    // Cast bars and channels loop their clip while they run (predicted bars included).
    const cast = this.canAct ? u.cast : null;
    if (cast) {
      const phase = cast.kind === 'channel' ? 'channel' : 'cast';
      const id = cast.abilityId ?? null;
      if (!(clips.phase === phase && clips.abilityId === id) && clips.recovering) {
        const clip = resolveClip(id, phase, this.classKey);
        if (clip) clips.play(clip, id, phase, this);
      }
    } else if (clips.phase === 'cast' || clips.phase === 'channel') clips.stop(LOOP_END_FADE, this);
    this.frameCueN = 0;
    clips.update(dtS, p.run, this);
    // Visual-only root motion on the body pivot (the root stays on the sim position).
    const o = clips.out;
    this.body.position.set(o[CH_LUNGE]!, o[CH_HOP]!, o[CH_SIDE]!);
    this.body.rotation.y = Math.PI / 2 + o[CH_SPIN]!;
    // +idle: the standing layer (rest / combat stance, weight shifts, breathing, look-around, fidgets, foot IK) on top of the clip.
    const idle = this.idle;
    const ii = this.idleIn;
    ii.dt = dtS;
    ii.run = p.run;
    ii.cast = p.cast;
    ii.inCombat = p.inCombat;
    ii.stun = p.stun;
    ii.dead = p.dead;
    ii.shuffle = p.shuffle;
    ii.armL = clips.armL;
    ii.armR = clips.armR;
    ii.clipW = clips.weight;
    idle.update(ii);
    const sum = this.pose;
    const io = idle.out;
    // +glide: the hanging pose and the landing settle on top (the arms hold the lines: the idle weapon hold gives way).
    const gl = this.glideLayer;
    const turn = Number.isFinite(this.glideFacing) && dtS > 0 ? Math.atan2(Math.sin(u.facing - this.glideFacing), Math.cos(u.facing - this.glideFacing)) / dtS : 0;
    this.glideFacing = u.facing;
    const av = this.airVel;
    gl.update({ dt: dtS, gliding: !!u.glide && u.alive, airborne: (u.jumpH ?? 0) > 0.05 && !u.perched, height: u.jumpH ?? 0, speed: Math.hypot(av.x, av.y), turnRate: turn, lateral: av.x * Math.sin(u.facing) - av.y * Math.cos(u.facing) });
    const go = gl.out;
    for (let i = 0; i < NCH; i++) sum.out[i] = o[i]! + io[i]! + go[i]!;
    sum.armL = Math.max(clips.armL, gl.w);
    sum.armR = Math.max(clips.armR, gl.w);
    if (gl.canopy > 0.01 && !this.glider) {
      this.glider = new GliderRig(this.glideTint);
      this.root.add(this.glider.group);
    }
    this.glider?.update(gl.canopy, gl.bank, Math.hypot(av.x, av.y), dtS);
    poseVrm(vrm, p, this.slot.side, this.slot.fz, this.poseKey, sum, idle); // +mage-specs: poseKey (staff hold); +idle
    // Blinks: random, sometimes doubled, and on big glances (anim/idle.ts); expressions are only re-applied when the weight changes.
    this.updater.setBlink(p.dead > 0.5 ? 1 : idle.blink); // +idle
    this.updater.springHz = this.ctx.springHz;
    this.updater.update(Math.min(dtS, 0.05));
    this.sampleTrails();
    this.placeCues();
  }

  /** Push the blade tip / inner edge of every open trail window (world space). */
  private sampleTrails(): void {
    const trails = this.ctx.shared.trails;
    for (let i = 0; i < MAX_WINDOWS; i++) {
      const h = this.trailIds[i]!;
      if (h < 0) continue;
      const a = this.anchors[this.trailHand[i] as 0 | 1];
      if (!a) continue;
      if (!a.holder) a.obj.updateWorldMatrix(true, false);
      TV_TIP.copy(a.tip).applyMatrix4(a.obj.matrixWorld);
      TV_BASE.copy(a.base).applyMatrix4(a.obj.matrixWorld);
      TV_BASE.sub(TV_TIP).multiplyScalar(this.trailWidth[i]!).add(TV_TIP);
      trails.push(h, TV_TIP, TV_BASE);
    }
  }

  /** Resolve this frame's cue markers to world points and hand them to the fx hooks. */
  private placeCues(): void {
    const hooks = this.ctx.clipFx;
    const n = this.frameCueN;
    this.frameCueN = 0;
    if (!hooks || n === 0) return;
    const f = this.facing;
    TV_DIR.set(Math.cos(f), 0, -Math.sin(f));
    const r = this.root.position;
    for (let i = 0; i < n; i++) {
      const c = this.frameCues[i]!;
      this.frameCues[i] = null;
      const p = TV_P;
      switch (c.at) {
        case 'tip':
        case 'hand': {
          const a = this.anchors[c.hand];
          const hand = this.slot.vrm.humanoid.getRawBoneNode(c.hand === 1 ? 'leftHand' : 'rightHand');
          if (c.at === 'tip' && a) {
            if (!a.holder) a.obj.updateWorldMatrix(true, false);
            p.copy(a.tip).applyMatrix4(a.obj.matrixWorld);
          } else if (hand) {
            hand.updateWorldMatrix(true, false);
            p.setFromMatrixPosition(hand.matrixWorld);
          } else p.set(r.x, this.height * 0.6, r.z);
          break;
        }
        case 'front':
          p.copy(r).addScaledVector(TV_DIR, 1.3);
          p.y = this.height * 0.5;
          break;
        case 'ground':
          p.copy(r).addScaledVector(TV_DIR, 1.2);
          p.y = 0.05;
          break;
        default:
          p.set(r.x, this.height * 0.55, r.z);
      }
      hooks.cue(this.id, c.fx, p, TV_DIR, c.color, c.scale);
    }
  }

  override dispose(): void {
    this.glider?.dispose(); // +glide
    this.glider = null;
    this.clips.reset(this);
    this.body.position.set(0, 0, 0);
    this.body.rotation.y = Math.PI / 2;
    const rig = LEG_RIG.get(this.slot.vrm); // +idle: pooled slots go back with the hips at rest
    if (rig) this.slot.vrm.humanoid.getNormalizedBoneNode('hips')?.position.copy(rig.hipRest);
    for (const h of this.slot.hulls) this.ctx.hulls.unit.delete(h);
    this.fall.rotation.set(0, 0, 0);
    this.ctx.assets.releaseVrm(this.slot);
    super.dispose();
  }
}

// ---------------------------------------------------------------------------
// KayKit mobs
// ---------------------------------------------------------------------------

const MOB_BONES = ['hips', 'spine', 'chest', 'head', 'upperarm.l', 'upperarm.r', 'lowerarm.l', 'lowerarm.r', 'upperleg.l', 'upperleg.r', 'lowerleg.l', 'lowerleg.r'] as const;
type MobBone = (typeof MOB_BONES)[number];

const AX_X = new THREE.Vector3(1, 0, 0);
const AX_Y = new THREE.Vector3(0, 1, 0);
const qA = new THREE.Quaternion();
const qP = new THREE.Quaternion();
const qM = new THREE.Quaternion();
const chain: THREE.Object3D[] = [];

export class MobView extends BaseView {
  private readonly model: THREE.Object3D;
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions: Record<'idle' | 'combat' | 'cast', THREE.AnimationAction>;
  private current: THREE.AnimationAction;
  private readonly bones: Partial<Record<MobBone, THREE.Object3D>> = {};
  private readonly rest = new Map<THREE.Object3D, THREE.Quaternion>();
  private readonly hand: THREE.Object3D | null;
  private deadFor = 0;
  /** +impact: per-mob material clones for the hit flash (made on the first hit; same programs). */
  private flashMats: THREE.MeshToonMaterial[] | null = null;
  private flashBase: THREE.Color[] = [];

  static create(u: RenderUnit, ctx: UnitCtx): MobView {
    const def = u.npcId ? ctx.npcDef(u.npcId) : undefined;
    const visual = def ? npcVisual(def) : npcVisualForTier(u.tier, u.color);
    const model = ctx.assets.clone(visual.model);
    if (!model) throw new Error(`model ${visual.model} not loaded`);
    return new MobView(u, ctx, visual, model);
  }

  private constructor(u: RenderUnit, ctx: UnitCtx, visual: MobVisual, model: THREE.Object3D) {
    super(u.id, KAYKIT_HEIGHT * visual.scale, u.radius, ctx);
    this.model = model;
    // +field: KayKit adventurers carry every weapon as a node; show only this mob's gear.
    if (visual.gear)
      for (const n of ADVENTURER_GEAR) {
        const o = model.getObjectByName(n);
        if (o) o.visible = visual.gear.includes(n);
      }
    for (const [w, slot] of visual.weapons) {
      const wm = ctx.assets.clone(w);
      const bone = model.getObjectByName(slot);
      if (wm && bone) bone.add(wm);
    }
    const tint = new THREE.Color(1, 1, 1).lerp(new THREE.Color(visual.tint), visual.tintAmount);
    stylize(model, ctx.hulls, { tint, glowColor: new THREE.Color(visual.glow), outline: true, castShadow: ctx.mobShadows, outlineRole: 'unit' });
    if (ctx.mobShadows) attachShadowProxy(model, visual.model);
    model.scale.setScalar(visual.scale);
    this.fall.add(model);
    this.hand = model.getObjectByName('handslot.r') ?? null;
    for (const n of MOB_BONES) {
      const b = model.getObjectByName(n);
      if (b) {
        this.bones[n] = b;
        this.rest.set(b, b.quaternion.clone());
      }
    }
    this.squashable = true;
    this.mixer = new THREE.AnimationMixer(model);
    const clips = ctx.assets.clips(visual.model);
    const clip = (name: string): THREE.AnimationClip | null => THREE.AnimationClip.findByName(clips, name) ?? THREE.AnimationClip.findByName(clips, 'Idle') ?? clips[0] ?? null;
    const mk = (name: string): THREE.AnimationAction => {
      const c = clip(name);
      return c ? this.mixer.clipAction(c) : this.mixer.clipAction(new THREE.AnimationClip('empty', 1, []));
    };
    this.actions = { idle: mk(visual.idleClip), combat: mk(visual.combatClip), cast: mk(visual.castClip) };
    this.current = this.actions.idle;
    this.current.play();
    this.current.time = hashId(u.id) * (this.current.getClip().duration || 1);
  }

  protected handWorld(out: THREE.Vector3): THREE.Vector3 | null {
    return this.hand ? this.hand.getWorldPosition(out) : null;
  }

  /**
   * +impact: hit flash through the toon materials' emissive term. The shared cached materials are
   * cloned for this mob on its first hit (clones keep the same onBeforeCompile, so they reuse the
   * compiled programs: no shader compile, no extra draw calls).
   */
  protected override applyFlash(f: number): void {
    if (!this.flashMats) {
      if (f <= 0) return;
      const mats: THREE.MeshToonMaterial[] = [];
      this.model.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh || o.userData['hull'] || Array.isArray(m.material)) return;
        const src = m.material as THREE.MeshToonMaterial;
        if (!src.isMeshToonMaterial) return;
        const c = src.clone();
        c.onBeforeCompile = src.onBeforeCompile;
        m.material = c;
        mats.push(c);
        this.flashBase.push(c.emissive.clone());
      });
      this.flashMats = mats;
    }
    const k = f * FLASH_MAX;
    const fc = this.flashColor;
    for (let i = 0; i < this.flashMats.length; i++) {
      const b = this.flashBase[i]!;
      this.flashMats[i]!.emissive.setRGB(b.r + fc.r * k, b.g + fc.g * k, b.b + fc.b * k);
    }
  }

  private play(next: THREE.AnimationAction): void {
    if (next === this.current) return;
    next.reset().play();
    next.crossFadeFrom(this.current, 0.22, false);
    this.current = next;
  }

  /** Rotate a bone about a model-space axis (additive on top of the clip pose). No matrix updates needed. */
  private turn(bone: THREE.Object3D | undefined, axis: THREE.Vector3, angle: number): void {
    if (!bone || angle === 0 || !bone.parent) return;
    // P = parent's rotation relative to the model root (product of local quaternions down the chain).
    qP.identity();
    chain.length = 0;
    for (let o: THREE.Object3D | null = bone.parent; o && o !== this.model; o = o.parent) chain.push(o);
    for (let i = chain.length - 1; i >= 0; i--) qP.multiply(chain[i]!.quaternion);
    qA.setFromAxisAngle(axis, angle);
    // local' = P^-1 * A * P * local
    qM.copy(qP).invert().multiply(qA).multiply(qP);
    bone.quaternion.premultiply(qM);
  }

  protected animate(u: RenderUnit, p: PoseParams, dtS: number): void {
    if (!u.alive) {
      this.deadFor += dtS;
      if (this.deadFor > 1.5) return; // frozen corpse: skip mixer work
    } else this.deadFor = 0;
    const want = p.cast > 0.5 ? this.actions.cast : p.inCombat ? this.actions.combat : this.actions.idle;
    if (u.alive) this.play(want);
    this.mixer.timeScale = p.stun > 0.5 ? 0.15 : 1;
    for (const [b, q] of this.rest) b.quaternion.copy(q);
    this.mixer.update(dtS);

    const B = this.bones;
    const run = p.run * (1 - p.stun) * (1 - p.dead);
    const s = Math.sin(p.runPhase);
    const c = Math.cos(p.runPhase);
    const sw = swingCurve(p.attackKind === 'swing' ? p.attack : -1);
    const spell = p.attackKind === 'spell' && p.attack >= 0 ? Math.sin(p.attack * Math.PI) : 0;
    // Forward swing of a down-pointing limb = negative rotation about model +X. Backpedal / side
    // steps take shorter strides (the cycle itself runs backwards, anim.ts); a turn in place shuffles.
    const legAmp = 0.75 * (1 - 0.2 * p.gaitBack - 0.5 * p.gaitSide); // +controls: the backpedal stride (gait BACK_STRIDE 0.8)
    const shuf = p.shuffle * (1 - run) * (1 - p.dead) * (1 - p.stun);
    const ss = Math.sin(p.shufflePhase);
    this.turn(B['upperleg.l'], AX_X, -s * legAmp * run - 0.3 * shuf * Math.max(0, ss));
    this.turn(B['upperleg.r'], AX_X, s * legAmp * run - 0.3 * shuf * Math.max(0, -ss));
    this.turn(B['lowerleg.l'], AX_X, run * (0.1 + 1.0 * Math.max(0, c)));
    this.turn(B['lowerleg.r'], AX_X, run * (0.1 + 1.0 * Math.max(0, -c)));
    this.turn(B['upperarm.l'], AX_X, s * 0.6 * run);
    this.turn(B['upperarm.r'], AX_X, -s * 0.6 * run - 2.2 * Math.max(0, sw.raise) - 0.8 * sw.strike - 1.3 * spell);
    this.turn(B['spine'], AX_X, run * 0.15 + p.stun * 0.25 - p.hit * 0.35 + sw.strike * 0.2);
    this.turn(B['spine'], AX_Y, p.twistSpine - sw.raise * 0.3);
    this.turn(B['chest'], AX_Y, -s * 0.18 * run);
    this.turn(B['head'], AX_X, p.stun * 0.45 - p.hit * 0.2);
    this.turn(B['head'], AX_Y, p.twistHead);
  }

  override dispose(): void {
    if (this.flashMats) for (const m of this.flashMats) m.dispose();
    this.flashMats = null;
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.model);
    this.model.traverse((o) => {
      if (o.userData['hull']) this.ctx.hulls.unit.delete(o);
    });
    super.dispose();
  }
}

// ---------------------------------------------------------------------------
// +field: procedural creatures (slimes, boars, wolves, crawlers, wisps)
// ---------------------------------------------------------------------------

const DEATH_FLOP = Math.PI / 2;

export class CreatureView extends BaseView {
  private readonly rig: CreatureRig;
  private readonly mesh: THREE.SkinnedMesh;
  private readonly mat: THREE.MeshToonMaterial;
  private readonly baseEmissive = new THREE.Color();
  private readonly sqz = { x: 1, y: 1 };
  private readonly halo: THREE.Sprite | null = null;
  private readonly haloMat: THREE.SpriteMaterial | null = null;
  private readonly scaleK: number;
  private flopSide = 1;

  constructor(u: RenderUnit, ctx: UnitCtx, visual: MobVisual) {
    const rig = buildCreature(visual.proc!, visual.tint, !!visual.spirit); // +ranger-specs: spirit beasts glow
    super(u.id, rig.height * visual.scale, u.radius, ctx);
    this.rig = rig;
    this.scaleK = visual.scale;
    // One owned material per creature (the hit flash tints its emissive); same program for all.
    this.mat = ownedToonMaterialFor(rig.glow, visual.spirit ? visual.tint : null);
    this.baseEmissive.copy(this.mat.emissive);
    this.mesh = new THREE.SkinnedMesh(rig.geometry, this.mat);
    this.mesh.add(rig.bones[0]!);
    this.mesh.bind(new THREE.Skeleton(rig.bones));
    this.mesh.castShadow = ctx.mobShadows;
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;
    this.mesh.userData['noStyle'] = true;
    this.mesh.scale.setScalar(visual.scale);
    this.fall.add(this.mesh);
    addHull(this.mesh, this.mat, ctx.hulls, 'unit');
    this.squashable = rig.kind !== 'wisp';
    if (rig.glow) {
      this.haloMat = new THREE.SpriteMaterial({ map: ctx.shared.glowTex, color: new THREE.Color(visual.tint), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0.55 });
      this.halo = new THREE.Sprite(this.haloMat);
      this.halo.scale.setScalar(2.2 * visual.scale);
      this.halo.position.y = 1.4 * visual.scale;
      this.fall.add(this.halo);
    }
    this.flopSide = hashId(u.id) < 0.5 ? 1 : -1;
  }

  protected handWorld(out: THREE.Vector3): THREE.Vector3 | null {
    return this.rig.bones[2]!.getWorldPosition(out);
  }

  protected override applyFlash(f: number): void {
    const k = f * FLASH_MAX;
    const c = this.flashColor;
    this.mat.emissive.setRGB(this.baseEmissive.r + c.r * k, this.baseEmissive.g + c.g * k, this.baseEmissive.b + c.b * k);
  }

  protected animate(u: RenderUnit, p: PoseParams, _dtS: number): void {
    poseCreature(this.rig, p, this.sqz);
    if (this.halo && this.haloMat) {
      this.halo.position.y = this.rig.bones[1]!.position.y * this.scaleK;
      this.haloMat.opacity = (0.45 + 0.12 * Math.sin(p.t * 5)) * (u.alive ? 1 : 1 - p.dead) * Math.min(1.2, this.ctx.shared.fx);
    }
  }

  override update(u: RenderUnit, dtS: number, timeMs: number): void {
    super.update(u, dtS, timeMs);
    const d = this.driverDead();
    if (d <= 0) {
      this.fall.rotation.z = 0;
      return;
    }
    // Death: critters flop onto their side (slimes melt flat, wisps sink and fade) instead of the
    // humanoid face-plant.
    this.fall.rotation.x = 0;
    const k = 1 - (1 - Math.min(1, d)) ** 3;
    if (this.rig.kind === 'slime' || this.rig.kind === 'bloom') {
      this.fall.scale.set(1 + 0.5 * k, 1 - 0.8 * k, 1 + 0.5 * k);
    } else if (this.rig.kind === 'wisp') {
      this.fall.position.y = -1.1 * k;
      this.fall.scale.setScalar(1 - 0.6 * k);
    } else {
      this.fall.rotation.z = this.flopSide * DEATH_FLOP * k;
      this.fall.position.y = 0.35 * k * (this.rig.kind === 'crawler' ? 0.3 : 1);
    }
  }

  private driverDead(): number {
    // The base pose already ran this frame: its fall angle encodes the death progress.
    return Math.min(1, Math.max(0, -this.fall.rotation.x / (Math.PI / 2 * 0.98)) ** (1 / 3));
  }

  override dispose(): void {
    this.mat.dispose();
    this.haloMat?.dispose();
    this.rig.geometry.dispose();
    this.mesh.skeleton.dispose();
    this.mesh.traverse((o) => {
      if (o.userData['hull']) this.ctx.hulls.unit.delete(o);
    });
    super.dispose();
  }
}

function ownedToonMaterialFor(glow: boolean, spiritTint: string | null = null): THREE.MeshToonMaterial {
  const m = toonColor(0xffffff).clone();
  m.vertexColors = true;
  m.onBeforeCompile = toonColor(0xffffff).onBeforeCompile;
  if (glow) m.emissive.setRGB(0.35, 0.55, 0.5);
  // +ranger-specs: a spirit beast glows in its own colour and reads as half there.
  if (spiritTint) {
    m.emissive.set(spiritTint).multiplyScalar(0.42);
    m.transparent = true;
    m.opacity = 0.86;
  }
  return m;
}

// ---------------------------------------------------------------------------
// Capsule fallback
// ---------------------------------------------------------------------------

export class CapsuleView implements UnitVisual {
  readonly blobR = 0;
  readonly root: THREE.Group;
  readonly hitbox: THREE.Mesh;
  readonly height: number;
  private readonly view: PlaceholderUnit;

  constructor(readonly id: UnitId, u: RenderUnit) {
    this.view = new PlaceholderUnit(id, u);
    this.root = this.view.group;
    this.hitbox = this.view.hitbox;
    this.height = this.view.height;
  }

  update(u: RenderUnit, _dtS: number, timeMs: number): void {
    this.view.update(u, timeMs);
  }

  cue(): void {}

  handPos(out: THREE.Vector3): THREE.Vector3 {
    this.root.updateMatrixWorld(true);
    return out.set(0.6, this.height * 0.6, 0).applyMatrix4(this.root.matrixWorld);
  }

  dispose(): void {
    this.root.removeFromParent();
    this.view.dispose();
  }
}

/** Build the best available view for a unit; never throws (falls back to a capsule). */
export function createUnitView(u: RenderUnit, ctx: UnitCtx): UnitVisual {
  try {
    if (u.kind === 'player') {
      const file = heroVrmFile(u);
      const slot = file ? ctx.assets.acquireVrmSync(file) : null;
      if (!slot) throw new Error(`no free VRM for ${u.classId ?? u.role ?? u.id}`);
      return new HeroView(u, ctx, slot);
    }
    const def = u.npcId ? ctx.npcDef(u.npcId) : undefined; // +field
    const vis = def ? npcVisual(def) : null;
    if (vis?.proc) return new CreatureView(u, ctx, vis);
    return MobView.create(u, ctx);
  } catch (err) {
    console.warn(`[toon] ${u.name} (${u.id}) uses a placeholder capsule:`, err instanceof Error ? err.message : err);
    return new CapsuleView(u.id, u);
  }
}

// Keep a tiny procedural material warm so brazier/prop fallbacks share programs.
export const PROP_FALLBACK = (): THREE.Material => toonColor(0x8a8296);

// ---------------------------------------------------------------------------
// +classes: procedural weapons (no KayKit asset fits)
// ---------------------------------------------------------------------------

/**
 * A toon recurve bow along weapon-local Y (the grip at the origin): two limbs that bow forward and
 * flick back at the tips, a leather-wrapped grip and a straight string. ≈ 2.2 KayKit units tall.
 */
function procWeapon(kind: ProcWeaponKind, tint?: THREE.Color, glow?: THREE.Color): THREE.Object3D {
  if (kind === 'greatsword') return greatswordMesh(tint, glow);
  if (kind === 'tower_shield') return towerShieldMesh(tint, glow);
  if (kind === 'revolver') return revolverMesh(tint, glow);
  if (kind === 'dagger') return daggerMesh(tint, glow); // +rogue-specs
  if (kind === 'vial') return vialMesh(tint, glow);
  if (kind === 'storm_staff' || kind === 'ice_staff' || kind === 'ember_staff') return mageStaffMesh(kind, tint, glow); // +mage-specs
  if (kind === 'censer' || kind === 'blood_censer' || kind === 'tome') return acolyteStaffMesh(kind, tint, glow); // +acolyte-specs
  const g = new THREE.Group();
  g.name = 'recurve_bow';
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= 24; i++) {
    const t = i / 24; // 0 = bottom tip, 1 = top tip
    const y = (t - 0.5) * 2.2;
    const a = Math.abs(t - 0.5) * 2; // 0 at the grip, 1 at the tips
    // Bow forward (+Z) along the limb, then recurve back at the very tips.
    const z = 0.32 * Math.sin(a * Math.PI * 0.62) - 0.22 * Math.max(0, a - 0.82) * 5;
    pts.push(new THREE.Vector3(0, y, z));
  }
  const curve = new THREE.CatmullRomCurve3(pts);
  const limb = new THREE.Mesh(new THREE.TubeGeometry(curve, 40, 0.045, 6, false), new THREE.MeshStandardMaterial({ color: 0x7a4a24, roughness: 0.8 }));
  const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.34, 8), new THREE.MeshStandardMaterial({ color: 0x3b2a1e, roughness: 0.9 }));
  const tipA = pts[0]!;
  const tipB = pts[pts.length - 1]!;
  const len = tipA.distanceTo(tipB);
  const string = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, len, 4), new THREE.MeshStandardMaterial({ color: 0xece4d0, roughness: 0.6 }));
  string.position.copy(tipA).add(tipB).multiplyScalar(0.5);
  for (const tip of [tipA, tipB]) {
    const nock = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 4), new THREE.MeshStandardMaterial({ color: 0xc9a14a, roughness: 0.5, metalness: 0.3 }));
    nock.position.copy(tip);
    g.add(nock);
  }
  g.add(limb, grip, string);
  return g;
}

/** +specs: procedural spec weapons. */
export type ProcWeaponKind = 'recurve_bow' | 'greatsword' | 'tower_shield' | 'revolver' | 'dagger' | 'vial' | 'storm_staff' | 'ice_staff' | 'ember_staff' | 'censer' | 'blood_censer' | 'tome'; // +rogue-specs: dagger, vial; +mage-specs: staves; +acolyte-specs: staves

/**
 * +rogue-specs: a long, slightly swept dagger along weapon-local Y (grip at the origin), ≈ 1.25
 * KayKit units: a dark blade in the spec's tint with a glowing edge, a short crossguard, a wrapped
 * grip and a gem pommel.
 */
function daggerMesh(tint = new THREE.Color(0.5, 0.45, 0.7), glow = new THREE.Color(0.75, 0.65, 1)): THREE.Object3D {
  const g = new THREE.Group();
  g.name = 'dagger';
  const steel = new THREE.Color(0.32, 0.32, 0.38).lerp(tint, 0.5);
  const shape = new THREE.Shape();
  shape.moveTo(-0.075, 0);
  shape.lineTo(0.07, 0);
  shape.quadraticCurveTo(0.09, 0.5, 0.02, 0.9);
  shape.lineTo(-0.035, 0.82);
  shape.quadraticCurveTo(-0.06, 0.45, -0.075, 0);
  const blade = new THREE.Mesh(new THREE.ExtrudeGeometry(shape, { depth: 0.03, bevelEnabled: false }).translate(0, 0, -0.015), std(steel, 0.3, 0.7));
  blade.position.y = 0.2;
  const edge = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.78, 0.04), new THREE.MeshBasicMaterial({ color: glow, transparent: true, opacity: 0.85 }));
  edge.position.set(0.072, 0.6, 0);
  edge.rotation.z = -0.07;
  const guard = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.05, 0.07), std(0x2a2630, 0.5, 0.5));
  guard.position.y = 0.18;
  const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.04, 0.3, 8), std(0x221a1e, 0.9, 0));
  grip.position.y = 0.02;
  const pommel = new THREE.Mesh(new THREE.OctahedronGeometry(0.055), new THREE.MeshBasicMaterial({ color: glow }));
  pommel.position.y = -0.16;
  g.add(blade, edge, guard, grip, pommel);
  return g;
}

/** +rogue-specs: the Venomwright's round alchemist's flask (neck along +Y) with glowing liquid. */
function vialMesh(tint = new THREE.Color(0.37, 0.68, 0.24), glow = new THREE.Color(0.72, 1, 0.48)): THREE.Object3D {
  const g = new THREE.Group();
  g.name = 'vial';
  const glass = new THREE.Mesh(new THREE.SphereGeometry(0.2, 14, 10), new THREE.MeshStandardMaterial({ color: 0xdfeee6, roughness: 0.1, metalness: 0, transparent: true, opacity: 0.35 }));
  glass.position.y = 0.22;
  const liquid = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 8, 0, Math.PI * 2, Math.PI * 0.35, Math.PI * 0.65), new THREE.MeshBasicMaterial({ color: glow.clone().lerp(tint, 0.35) }));
  liquid.position.y = 0.22;
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.07, 0.18, 10), new THREE.MeshStandardMaterial({ color: 0xdfeee6, roughness: 0.1, transparent: true, opacity: 0.4 }));
  neck.position.y = 0.46;
  const cork = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.05, 0.08, 8), std(0x7a5634, 0.9, 0));
  cork.position.y = 0.58;
  const band = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.018, 6, 18), std(tint, 0.4, 0.6));
  band.position.y = 0.22;
  band.rotation.x = Math.PI / 2;
  g.add(liquid, glass, neck, cork, band);
  return g;
}

const std = (color: THREE.ColorRepresentation, roughness = 0.6, metalness = 0.2): THREE.MeshStandardMaterial => new THREE.MeshStandardMaterial({ color, roughness, metalness });

/** A two-handed greatsword along weapon-local Y (grip at the origin), ≈ 2.6 KayKit units: blade in the spec's tint with a glowing edge. */
function greatswordMesh(tint = new THREE.Color(0.75, 0.78, 0.85), glow = new THREE.Color(1, 0.6, 0.4)): THREE.Object3D {
  const g = new THREE.Group();
  g.name = 'greatsword';
  const steel = new THREE.Color(0.72, 0.74, 0.8).lerp(tint, 0.45);
  const blade = new THREE.Mesh(new THREE.BoxGeometry(0.2, 2.0, 0.05), std(steel, 0.35, 0.6));
  blade.position.y = 1.3;
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.142, 0.32, 4), std(steel, 0.35, 0.6));
  tip.position.y = 2.46;
  tip.rotation.y = Math.PI / 4;
  tip.scale.set(1, 1, 0.35);
  const edge = new THREE.Mesh(new THREE.BoxGeometry(0.03, 1.9, 0.06), new THREE.MeshBasicMaterial({ color: glow, transparent: true, opacity: 0.8 }));
  edge.position.set(0.105, 1.28, 0);
  const guard = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.09, 0.12), std(0x3a3036, 0.5, 0.5));
  guard.position.y = 0.27;
  const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.5, 8), std(0x2a1d18, 0.9, 0));
  grip.position.y = 0;
  const pommel = new THREE.Mesh(new THREE.SphereGeometry(0.075, 8, 6), std(tint, 0.4, 0.6));
  pommel.position.y = -0.28;
  g.add(blade, tip, edge, guard, grip, pommel);
  return g;
}

/** A tall iron tower shield (face +Z, top +Y): riveted plates in the spec's tint. */
function towerShieldMesh(tint = new THREE.Color(0.6, 0.65, 0.72), glow = new THREE.Color(0.85, 0.9, 1)): THREE.Object3D {
  const g = new THREE.Group();
  g.name = 'tower_shield';
  const face = new THREE.Mesh(new THREE.BoxGeometry(0.95, 1.5, 0.08), std(new THREE.Color(0.32, 0.34, 0.38).lerp(tint, 0.35), 0.55, 0.55));
  const rim = new THREE.Mesh(new THREE.BoxGeometry(1.05, 1.6, 0.05), std(0x2b2d33, 0.5, 0.6));
  rim.position.z = -0.03;
  g.add(rim, face);
  // Three plate bands with rivets (the Plating motif).
  for (let i = -1; i <= 1; i++) {
    const band = new THREE.Mesh(new THREE.BoxGeometry(0.98, 0.07, 0.03), std(tint, 0.4, 0.7));
    band.position.set(0, i * 0.45, 0.055);
    g.add(band);
    for (const x of [-0.38, 0, 0.38]) {
      const rivet = new THREE.Mesh(new THREE.SphereGeometry(0.035, 6, 4), new THREE.MeshBasicMaterial({ color: glow }));
      rivet.position.set(x, i * 0.45, 0.075);
      g.add(rivet);
    }
  }
  return g;
}

/**
 * The Deadeye's long-barrelled flintlock revolver (toon style), barrel along weapon-local Y out of
 * the fist: a walnut grip angled back, a brass-banded six-chamber cylinder, a hammer and a long
 * octagonal barrel with a front sight.
 */
export function revolverMesh(tint = new THREE.Color(0.79, 0.6, 0.36), glow = new THREE.Color(1, 0.88, 0.66)): THREE.Object3D {
  const g = new THREE.Group();
  g.name = 'revolver';
  const iron = std(0x3a3a40, 0.45, 0.7);
  const brass = std(tint, 0.35, 0.8);
  const wood = std(0x5a3520, 0.85, 0);
  const grip = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.42, 0.12), wood);
  grip.position.set(-0.1, -0.12, 0);
  grip.rotation.z = 0.5;
  const frame = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.28, 0.1), iron);
  frame.position.y = 0.14;
  const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.2, 6), brass);
  drum.position.y = 0.2;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const chamber = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.205, 6), new THREE.MeshBasicMaterial({ color: 0x15151a }));
    chamber.position.set(Math.cos(a) * 0.065, 0.2, Math.sin(a) * 0.065);
    g.add(chamber);
  }
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.05, 0.9, 8), iron);
  barrel.position.y = 0.75;
  const band = new THREE.Mesh(new THREE.CylinderGeometry(0.058, 0.058, 0.05, 8), brass);
  band.position.y = 1.12;
  const sight = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.05, 0.03), new THREE.MeshBasicMaterial({ color: glow }));
  sight.position.set(0.055, 1.15, 0);
  const hammer = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.1, 0.04), iron);
  hammer.position.set(-0.06, 0.32, 0);
  hammer.rotation.z = -0.5;
  const guard = new THREE.Mesh(new THREE.TorusGeometry(0.06, 0.012, 4, 10, Math.PI), brass);
  guard.position.set(0.02, 0.02, 0);
  guard.rotation.z = Math.PI;
  g.add(grip, frame, drum, barrel, band, sight, hammer, guard);
  return g;
}

/**
 * +mage-specs: the Mage specs' staves along weapon-local Y, the fist at the origin (a third of the way
 * up the shaft), ≈ 2.7 KayKit units. Shaft and fittings in the spec's tint, the head in its glow:
 *  - storm: dark iron with a forked crown cradling a crackling orb and zigzag sparks;
 *  - ice: pale wood under a cluster of frost crystals;
 *  - ember: charred wood, a three-prong cage around a burning core and a lick of flame.
 */
function mageStaffMesh(kind: 'storm_staff' | 'ice_staff' | 'ember_staff', tint = new THREE.Color(0.4, 0.5, 1), glow = new THREE.Color(0.8, 0.9, 1)): THREE.Object3D {
  const g = new THREE.Group();
  g.name = kind;
  const storm = kind === 'storm_staff';
  const wood = storm ? 0x2a2c34 : kind === 'ice_staff' ? 0xb9c8d4 : 0x2b1a12;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.06, 2.4, 8), std(wood, storm ? 0.45 : 0.8, storm ? 0.6 : 0));
  shaft.position.y = 0.35;
  const butt = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.16, 6), std(tint, 0.4, 0.6));
  butt.position.y = -0.92;
  butt.rotation.x = Math.PI;
  const bandA = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.06, 8), std(tint, 0.35, 0.7));
  bandA.position.y = 1.35;
  const bandB = bandA.clone();
  bandB.position.y = 0.22;
  g.add(shaft, butt, bandA, bandB);
  const glowMat = (o = 0.9): THREE.MeshBasicMaterial => new THREE.MeshBasicMaterial({ color: glow, transparent: true, opacity: o });
  const top = 1.55;
  if (storm) {
    for (const sx of [-1, 1]) {
      const prong = new THREE.Mesh(new THREE.ConeGeometry(0.035, 0.5, 5), std(tint, 0.35, 0.75));
      prong.position.set(sx * 0.11, top + 0.2, 0);
      prong.rotation.z = -sx * 0.35;
      g.add(prong);
      for (let i = 0; i < 3; i++) {
        const zig = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.14, 0.018), glowMat(0.85));
        zig.position.set(sx * (0.2 + (i % 2) * 0.05), top + 0.05 + i * 0.12, 0);
        zig.rotation.z = (i % 2 ? 0.6 : -0.6) * sx;
        g.add(zig);
      }
    }
    const orb = new THREE.Mesh(new THREE.SphereGeometry(0.12, 12, 8), glowMat(0.95));
    orb.position.y = top + 0.22;
    const halo = new THREE.Mesh(new THREE.SphereGeometry(0.19, 12, 8), new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.3, depthWrite: false }));
    halo.position.copy(orb.position);
    g.add(orb, halo);
  } else if (kind === 'ice_staff') {
    const crystals: readonly [number, number, number, number][] = [
      [0, 0.32, 0, 0.5],
      [0.1, 0.18, 0.04, 0.32],
      [-0.09, 0.16, -0.03, 0.3],
      [0.02, 0.12, -0.1, 0.26],
    ];
    for (const [x, y, z, h] of crystals) {
      const c = new THREE.Mesh(new THREE.ConeGeometry(0.06, h, 4), new THREE.MeshStandardMaterial({ color: glow, emissive: tint, emissiveIntensity: 0.35, roughness: 0.15, metalness: 0.1, transparent: true, opacity: 0.88 }));
      c.position.set(x, top + y, z);
      c.rotation.z = -x * 2.2;
      c.rotation.x = z * 2.2;
      g.add(c);
    }
    const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.07), glowMat(0.95));
    core.position.y = top + 0.12;
    g.add(core);
  } else {
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      const prong = new THREE.Mesh(new THREE.TorusGeometry(0.15, 0.018, 4, 10, Math.PI * 0.9), std(tint, 0.45, 0.55));
      prong.position.set(Math.cos(a) * 0.05, top + 0.16, Math.sin(a) * 0.05);
      prong.rotation.set(0, -a, Math.PI / 2);
      g.add(prong);
    }
    const core = new THREE.Mesh(new THREE.SphereGeometry(0.1, 10, 8), new THREE.MeshBasicMaterial({ color: new THREE.Color(1, 0.55, 0.15).lerp(glow, 0.35) }));
    core.position.y = top + 0.17;
    const flame = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.26, 7), glowMat(0.7));
    flame.position.y = top + 0.36;
    g.add(core, flame);
  }
  return g;
}

/**
 * +acolyte-specs: the Acolyte staves, along weapon-local Y (grip at the origin), ≈ 2.4 KayKit units:
 *  - censer (Lightbinder): a pale-gold staff crowned with a sunburst ring and a glowing censer orb;
 *  - blood_censer (Hemomancer): a dark staff wound with thorns, a blood-red orb in a clawed cradle;
 *  - tome (Heretic): a gnarled black staff with a hollow violet eye between two hooked horns.
 */
function acolyteStaffMesh(kind: 'censer' | 'blood_censer' | 'tome', tint = new THREE.Color(1, 0.82, 0.4), glow = new THREE.Color(1, 0.95, 0.77)): THREE.Object3D {
  const g = new THREE.Group();
  g.name = kind;
  const wood = kind === 'censer' ? 0xe8dcc0 : kind === 'blood_censer' ? 0x2a0d12 : 0x1c1424;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.06, 2.4, 8), std(wood, 0.6, kind === 'censer' ? 0.35 : 0.1));
  shaft.position.y = 0.35;
  const band = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.06, 8), std(tint, 0.35, 0.7));
  band.position.y = 1.35;
  const band2 = band.clone();
  band2.position.y = 0.2;
  g.add(shaft, band, band2);
  const glowMat = (o = 0.9): THREE.MeshBasicMaterial => new THREE.MeshBasicMaterial({ color: glow, transparent: true, opacity: o });
  const top = 1.55;
  if (kind === 'censer') {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.025, 6, 20), std(tint, 0.3, 0.8));
    ring.position.y = top + 0.22;
    g.add(ring);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const ray = new THREE.Mesh(new THREE.ConeGeometry(0.025, 0.14, 4), std(tint, 0.3, 0.8));
      ray.position.set(Math.cos(a) * 0.27, top + 0.22 + Math.sin(a) * 0.27, 0);
      ray.rotation.z = a - Math.PI / 2;
      g.add(ray);
    }
    const orb = new THREE.Mesh(new THREE.SphereGeometry(0.1, 12, 8), glowMat(0.95));
    orb.position.y = top + 0.22;
    g.add(orb);
  } else if (kind === 'blood_censer') {
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      const claw = new THREE.Mesh(new THREE.ConeGeometry(0.03, 0.34, 5), std(tint, 0.45, 0.4));
      claw.position.set(Math.cos(a) * 0.1, top + 0.18, Math.sin(a) * 0.1);
      claw.rotation.set(Math.sin(a) * 0.5, 0, -Math.cos(a) * 0.5);
      g.add(claw);
    }
    for (let i = 0; i < 6; i++) {
      const thorn = new THREE.Mesh(new THREE.ConeGeometry(0.018, 0.1, 4), std(tint, 0.5, 0.3));
      const a = i * 2.1;
      thorn.position.set(Math.cos(a) * 0.055, 0.5 + i * 0.16, Math.sin(a) * 0.055);
      thorn.rotation.set(Math.sin(a) * 1.3, 0, -Math.cos(a) * 1.3);
      g.add(thorn);
    }
    const orb = new THREE.Mesh(new THREE.SphereGeometry(0.11, 12, 8), new THREE.MeshBasicMaterial({ color: new THREE.Color(0.75, 0.05, 0.12).lerp(glow, 0.25) }));
    orb.position.y = top + 0.16;
    const halo = new THREE.Mesh(new THREE.SphereGeometry(0.17, 12, 8), new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.3, depthWrite: false }));
    halo.position.copy(orb.position);
    g.add(orb, halo);
  } else {
    for (const sx of [-1, 1]) {
      const horn = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.025, 5, 10, Math.PI * 0.8), std(0x2b2236, 0.5, 0.2));
      horn.position.set(sx * 0.06, top + 0.12, 0);
      horn.rotation.z = sx > 0 ? -0.2 : Math.PI + 0.2;
      g.add(horn);
    }
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.09, 12, 8), glowMat(0.95));
    eye.position.y = top + 0.2;
    eye.scale.set(1, 0.7, 1);
    const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.04, 8, 6), new THREE.MeshBasicMaterial({ color: 0x0b0612 }));
    pupil.position.set(0, top + 0.2, 0.07);
    const halo = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 8), new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.28, depthWrite: false }));
    halo.position.copy(eye.position);
    g.add(eye, pupil, halo);
  }
  return g;
}
